import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import LeadgenPreloader from '../../ui/LeadgenPreloader';
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import './UnifiedLeadGenView.css';

// We'll import sub-components or full views for results
import ProspectFinderResults from '../prospecting/ProspectFinderResults';
import B2CRecommendationView from '../prospecting/B2CRecommendationView';
import MarketAnalysisChatView from '../analysis/MarketAnalysisChatView';
// MarketAnalysisView overlay removed — all processing now happens inline in chat

import industryData from '../../data/industryData.json';
import countriesData from '../../data/countriesData.json';

const UnifiedLeadGenView = ({ userId, refreshProfile }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Analysis your query...');
  const [activeMode, setActiveMode] = useState('auto'); // auto, search_prospect, search_company, search_persona, market_analysis

  // Multi-step animated progress for chat thinking bubble
  const [progressSteps, setProgressSteps] = useState([]);
  const [activeProgressStep, setActiveProgressStep] = useState(0);
  const [isPollingInChat, setIsPollingInChat] = useState(false); // for market/b2c post-submit polling
  const ignoreModeReset = useRef(false);
  const isAutoModeRef = useRef(false);
  const activeBriefRef = useRef(null);
  const textareaRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isFilterClosing, setIsFilterClosing] = useState(false);
  const [isMultiline, setIsMultiline] = useState(true);
  const [searchResult, setSearchResult] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // B2B Filter State
  const [searchType, setSearchType] = useState('prospects');
  const [filters, setFilters] = useState({
    contactName: [],
    jobTitles: [],
    contactLocations: [],
    managementLevels: [],
    departments: [],
    companyNames: [],
    companyLocations: [],
    companySizes: [],
    industries: [],
    revenueMin: '',
    revenueMax: '',
  });

  const [b2bResults, setB2bResults] = useState(null);
  const [b2cTask, setB2cTask] = useState(null);
  const [marketAnalysisId, setMarketAnalysisId] = useState(null);
  const [showProcessingToast, setShowProcessingToast] = useState(false);

  // --- Chat & Session Migration ---
  const [activeChatRoomId, setActiveChatRoomId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentRoomName, setCurrentRoomName] = useState('');
  const [showListResults, setShowListResults] = useState(false);
  const [expandedResultId, setExpandedResultId] = useState(null);
  const [completedAnalysisIds, setCompletedAnalysisIds] = useState(new Set());
  const [simulatingBriefId, setSimulatingBriefId] = useState(null);
  const [executingBriefId, setExecutingBriefId] = useState(null);
  const [expandedSynonymsId, setExpandedSynonymsId] = useState(null);
  const isChatMode = !!activeChatRoomId || chatMessages.length > 0;
  const isSearchLoading = loading;

  // --- Navigation guard ---
  const { setProcessing, bypassOnce } = useNavigationGuard();

  // If the last assistant message already has a terminal status, stop guarding
  const lastAIMsg = chatMessages.filter((m) => m.isAi).slice(-1)[0];
  const isLastMsgDone =
    lastAIMsg?.metadata?.status === 'completed' ||
    lastAIMsg?.metadata?.status === 'failed' ||
    lastAIMsg?.metadata?.status === 'brief_ready';
  const processingActive = !isLastMsgDone && (loading || isPollingInChat);

  // Sync guard to processingActive (async backup — keeps guard in sync across all code paths)
  useEffect(() => {
    setProcessing(processingActive);
  }, [processingActive, setProcessing]);

  // Show/hide processing toast during loading or polling
  useEffect(() => {
    setShowProcessingToast(processingActive);
  }, [processingActive]);

  // Auto-dismiss processing toast after 7 seconds
  useEffect(() => {
    if (!showProcessingToast) return;
    const timer = setTimeout(() => setShowProcessingToast(false), 7000);
    return () => clearTimeout(timer);
  }, [showProcessingToast]);

  // beforeunload for browser refresh/close
  useEffect(() => {
    if (!processingActive) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [processingActive]);

  // Cleanup guard on unmount
  useEffect(() => {
    return () => setProcessing(false);
  }, [setProcessing]);

  const isLikelyMarketMetadata = (metadata, text = '') => {
    if (!metadata) return false;

    if (
      metadata.isMarketAnalysis ||
      metadata._intent === 'market_analysis' ||
      metadata._taskType === 'market_analysis'
    ) {
      return true;
    }

    if (
      metadata.contacts ||
      metadata.companies ||
      metadata.task ||
      metadata.isB2CTask ||
      metadata.ideal_persona ||
      metadata.product_description
    ) {
      return false;
    }

    const hasAnalysisId = !!(metadata.requestId || metadata.id || metadata.data?.id);
    if (!hasAnalysisId) return false;

    const dataMessage = typeof metadata.data?.message === 'string' ? metadata.data.message : '';
    return (
      /market analysis|market research/i.test(text || '') ||
      /market analysis|market research/i.test(dataMessage)
    );
  };

  const isLikelyB2CMetadata = (metadata) => {
    if (!metadata) return false;
    if (
      metadata.isB2CTask ||
      metadata._intent === 'b2c_persona' ||
      metadata._intent === 'search_persona' ||
      metadata._taskType === 'b2c_persona'
    )
      return true;
    // Check for presence of B2C-specific fields or the data ID during polling
    return !!(
      metadata.task ||
      metadata.ideal_persona ||
      metadata.product_description ||
      (metadata.data?.id && !metadata.isMarketAnalysis)
    );
  };

  const pushAssistantError = (message) => {
    setChatMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        isAi: true,
        isError: true,
        text: message,
      },
    ]);
  };

  // Optional: detect room id from url
  useEffect(() => {
    const pathSegments = window.location.pathname.split('/');
    setShowFilters(false); // Close filters on any path change
    if (pathSegments[1] === 'discover' && pathSegments[2]) {
      setActiveChatRoomId(pathSegments[2]);
    } else {
      setActiveChatRoomId(null);
      setChatMessages([]);
      setB2bResults(null);
      setB2cTask(null);
      setMarketAnalysisId(null);
      setCurrentRoomName('');
      resetFilters(); // Definitively isolate new search sessions by clearing old criteria
    }
  }, [window.location.pathname]);

  const isInternalReset = useRef(false);

  const fetchChatMessages = async (roomId = activeChatRoomId) => {
    if (!roomId) return;
    try {
      const res = await axios.get(`/chat/${roomId}/messages`);
      if (res.data.success) {
        const messages = res.data.data.map((msg) => ({
          id: msg.id,
          isAi: msg.role === 'assistant',
          text: msg.content,
          metadata: msg.metadata,
          isError: msg.type === 'error',
        }));
        setChatMessages(messages);
        const lastBriefFromDb = [...messages].reverse().find((m) => m.metadata?.searchBrief);
        if (lastBriefFromDb) {
          activeBriefRef.current = lastBriefFromDb.metadata.searchBrief;
        }

        // --- Task Continuity Fix ---
        // If the last AI message corresponds to an active background task, re-trigger polling
        const lastAiWithTask = [...messages]
          .reverse()
          .find(
            (m) =>
              m.isAi &&
              m.metadata &&
              (m.metadata.requestId || m.metadata.id || m.metadata.data?.id || m.metadata.task)
          );

        if (lastAiWithTask) {
          const taskMetadata = lastAiWithTask.metadata;
          const analysisId = taskMetadata.requestId || taskMetadata.id || taskMetadata.data?.id;
          const b2cTaskData = taskMetadata.task || taskMetadata.data;
          const isMarketTask = isLikelyMarketMetadata(taskMetadata, lastAiWithTask.text);
          const isB2CTask = isLikelyB2CMetadata(taskMetadata);

          if (analysisId && isMarketTask) {
            // Potential Market Analysis
            try {
              const statusRes = await axios.get(`/market-analysis/${analysisId}`);
              if (statusRes.data.success && statusRes.data.data.status === 'processing') {
                setMarketAnalysisId(analysisId);
                setIsPollingInChat(true);
                const steps = [
                  { name: 'Analyzing query...', icon: 'fa-search', status: 'done' },
                  { name: 'Searching web...', icon: 'fa-globe', status: 'active' },
                  { name: 'Generating pdf...', icon: 'fa-file-pdf', status: 'pending' },
                ];
                setProgressSteps(steps);
                setActiveProgressStep(1);
              }
            } catch (e) {
              console.warn('Task recovery failed:', e.message);
            }
          } else if (isB2CTask && b2cTaskData && b2cTaskData.id) {
            // B2C Task recovery
            try {
              const statusRes = await axios.get(`/b2c/recommendations/${b2cTaskData.id}`);
              if (
                statusRes.data.success &&
                (statusRes.data.data.status === 'processing' ||
                  statusRes.data.data.status === 'initializing')
              ) {
                setB2cTask(statusRes.data.data);
                setIsPollingInChat(true);
                setProgressSteps([
                  { name: 'Analysis your query...', icon: 'fa-search', status: 'done' },
                  {
                    name: 'Identify the customer personal...',
                    icon: 'fa-user-tag',
                    status: 'active',
                  },
                  { name: 'Searching from web...', icon: 'fa-globe', status: 'pending' },
                  { name: 'Generate responses...', icon: 'fa-comment-dots', status: 'pending' },
                ]);
                setActiveProgressStep(1);
              }
            } catch (e) {}
          }
        }
      }
      const roomsRes = await axios.get('/chat');
      if (roomsRes.data.success) {
        const room = roomsRes.data.data.find((r) => r.id === roomId);
        if (room) {
          setCurrentRoomName(room.name);
        }
      }
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
    }
  };

  // Fetch history
  useEffect(() => {
    // Isolation Fix: Reset all results and messages immediately when switching rooms
    // to prevent flickering data from the previous session while the new one loads.
    // Skip reset if this was triggered internally by handleSearch for a new room
    if (isInternalReset.current) {
      isInternalReset.current = false;
      return;
    }

    setChatMessages([]);
    setB2bResults(null);
    setB2cTask(null);
    setMarketAnalysisId(null);
    setLoading(false);

    if (!activeChatRoomId) return;

    fetchChatMessages(activeChatRoomId);
  }, [activeChatRoomId]);

  const [activeAnalyses, setActiveAnalyses] = useState([]);

  // Filter UI States
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [expandedIndustries, setExpandedIndustries] = useState({});
  const [industrySearch, setIndustrySearch] = useState('');
  const [contactLocationSearch, setContactLocationSearch] = useState('');
  const [companyLocationSearch, setCompanyLocationSearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [senioritySearch, setSenioritySearch] = useState('');
  const [sizeSearch, setSizeSearch] = useState('');
  const [revenueSearch, setRevenueSearch] = useState('');

  // Typing animation state
  const [placeholder, setPlaceholder] = useState('');
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  // Processing placeholder dot animation
  const [processingDotCount, setProcessingDotCount] = useState(3);
  const processingDotsRef = useRef(null);

  useEffect(() => {
    if (processingActive) {
      processingDotsRef.current = setInterval(() => {
        setProcessingDotCount((prev) => (prev >= 6 ? 3 : prev + 1));
      }, 500);
    } else {
      setProcessingDotCount(3);
    }
    return () => {
      if (processingDotsRef.current) {
        clearInterval(processingDotsRef.current);
        processingDotsRef.current = null;
      }
    };
  }, [processingActive]);

  const suggestions = [
    'Find CEOs in London...',
    'Analyze the EV market in Malaysia...',
    'Who is the ideal buyer for organic coffee subscription?',
    'Marketing directors in SaaS companies in Berlin...',
    'Competitive landscape of EV chargers in UK...',
  ];

  useEffect(() => {
    const handleTyping = () => {
      const fullText = suggestions[suggestionIndex];
      setPlaceholder((prev) =>
        isDeleting ? fullText.substring(0, prev.length - 1) : fullText.substring(0, prev.length + 1)
      );

      setTypingSpeed(isDeleting ? 50 : 100);

      if (!isDeleting && placeholder === fullText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && placeholder === '') {
        setIsDeleting(false);
        setSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [placeholder, isDeleting, suggestionIndex, typingSpeed]);

  const getSummaryRange = (val1, val2, type) => {
    if (type === 'size') {
      const selectedArr = val1;
      if (!selectedArr || selectedArr.length === 0) return '';
      return selectedArr.join(', ');
    } else if (type === 'revenue') {
      const min = val1;
      const max = val2;
      if (!min && !max) return '';
      if (min && max) return `${min} - ${max}`;
      if (min) return `${min}+`;
      if (max) return `up to ${max}`;
    }
    return '';
  };

  const addFilter = (key, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) return;
    setFilters((prev) => {
      const val = typeof value === 'string' ? value.trim() : value;
      const current = prev[key] || [];
      if (current.includes(val)) return prev;
      return { ...prev, [key]: [...current, val] };
    });
  };

  const constructSentence = () => {
    const {
      contactName,
      jobTitles,
      contactLocations,
      managementLevels,
      departments,
      companyNames,
      companyLocations,
      companySizes,
      industries,
      revenueMin,
      revenueMax,
    } = filters;

    // Check if any filter is actually applied
    const hasAppliedFilters = Object.values(filters).some((arr) => arr && arr.length > 0);
    if (!hasAppliedFilters) return '';

    // If any filters are applied, start building the sentence
    if (searchType === 'companies') {
      let sentence = 'Please find ';
      const displayIndustries = [];
      if (industries && industries.length > 0) {
        industryData.forEach((parent) => {
          const hasMain = industries.includes(`${parent.main_industry} (Main)`);
          const allSubsSelected =
            parent.sub_industries &&
            parent.sub_industries.length > 0 &&
            parent.sub_industries.every((s) => industries.includes(`${s.value} (Sub)`));
          if (hasMain || allSubsSelected) {
            displayIndustries.push(parent.main_industry);
          } else {
            parent.sub_industries?.forEach((s) => {
              if (industries.includes(`${s.value} (Sub)`)) displayIndustries.push(s.value);
            });
          }
        });
      }

      if (companyNames && companyNames.length > 0) {
        sentence += `${companyNames.join(', ')} Company`;
      } else if (displayIndustries.length > 0) {
        sentence += `companies in the ${displayIndustries.join(', ')} industry`;
      } else {
        sentence += 'companies';
      }

      // If we have both names AND industries, add the industry part if not already added
      if (companyNames && companyNames.length > 0 && displayIndustries.length > 0) {
        sentence += ` in the ${displayIndustries.join(', ')} industry`;
      }

      if (companyLocations && companyLocations.length > 0) {
        sentence += ` based in ${companyLocations.join(', ')}`;
      }

      if (companySizes && companySizes.length > 0) {
        sentence += ` with ${getSummaryRange(companySizes, null, 'size')} employees`;
      }

      if (revenueMin || revenueMax) {
        sentence += ` generating around ${getSummaryRange(revenueMin, revenueMax, 'revenue')} revenue`;
      }

      return sentence.trim() + '.';
    }

    // Default Prospect Flow
    let sentence = 'Please find ';

    if (contactName && contactName.length > 0) {
      sentence += contactName.join(', ');
    } else {
      sentence += searchType;
    }

    if (jobTitles && jobTitles.length > 0) {
      sentence += `, who is working as a ${jobTitles.join(', ')}`;
    }

    if (departments && departments.length > 0) {
      sentence += ` in the ${departments.join(', ')} department`;
    }

    if (managementLevels && managementLevels.length > 0) {
      sentence += ` at a ${managementLevels.join(', ')} level`;
    }

    if (contactLocations && contactLocations.length > 0) {
      sentence += `, based in ${contactLocations.join(', ')}`;
    }

    if (companyNames && companyNames.length > 0) {
      sentence += `, at ${companyNames.join(', ')}`;
    }

    if (companyLocations && companyLocations.length > 0) {
      sentence += `, at companies in ${companyLocations.join(', ')}`;
    }

    if (industries && industries.length > 0) {
      const displayIndustries = [];
      industryData.forEach((parent) => {
        const hasMain = industries.includes(`${parent.main_industry} (Main)`);
        const allSubsSelected =
          parent.sub_industries &&
          parent.sub_industries.length > 0 &&
          parent.sub_industries.every((s) => industries.includes(`${s.value} (Sub)`));

        if (hasMain || allSubsSelected) {
          displayIndustries.push(parent.main_industry);
        } else {
          parent.sub_industries?.forEach((s) => {
            if (industries.includes(`${s.value} (Sub)`)) {
              displayIndustries.push(s.value);
            }
          });
        }
      });
      const industryList = displayIndustries.join(', ');
      sentence += `, a company in the ${industryList} industry`;
    }

    if (companySizes && companySizes.length > 0) {
      sentence += ` with approximately ${getSummaryRange(companySizes, null, 'size')} employees`;
    }

    if (revenueMin || revenueMax) {
      sentence += ` and generating around ${getSummaryRange(revenueMin, revenueMax, 'revenue')} in revenue`;
    }

    return sentence.trim() + '.';
  };

  const resetFilters = () => {
    setFilters({
      contactName: [],
      jobTitles: [],
      contactLocations: [],
      managementLevels: [],
      departments: [],
      companyNames: [],
      companyLocations: [],
      companySizes: [],
      industries: [],
      revenueMin: '',
      revenueMax: '',
    });
    setSearchQuery('');
  };

  useEffect(() => {
    if (['b2b', 'search_prospect', 'search_company'].includes(activeMode)) {
      const sentence = constructSentence();
      if (searchQuery !== sentence) {
        setSearchQuery(sentence);
      }
    }
  }, [filters, activeMode, searchType]);

  useEffect(() => {
    const updateTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const content = (searchQuery || '').trim();

      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 250;

      if (scrollHeight > maxHeight) {
        textarea.style.height = maxHeight + 'px';
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = Math.max(scrollHeight, 40) + 'px';
        textarea.style.overflowY = 'hidden';
      }
    };

    updateTextareaHeight();
    const timer = setTimeout(updateTextareaHeight, 10);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const removeFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((v) => v !== value),
    }));
  };

  const handleIndustryToggle = (item, isMain, isCheckedOverride) => {
    setFilters((prev) => {
      let newIndustries = [...(prev.industries || [])];

      if (isMain) {
        const mainLabel = `${item.main_industry} (Main)`;
        const isCurrentlyChecked = newIndustries.includes(mainLabel);
        const targetChecked =
          isCheckedOverride !== undefined ? isCheckedOverride : !isCurrentlyChecked;

        const subLabels = (item.sub_industries || []).map((s) => `${s.value} (Sub)`);

        if (targetChecked) {
          if (!newIndustries.includes(mainLabel)) newIndustries.push(mainLabel);
          subLabels.forEach((s) => {
            if (!newIndustries.includes(s)) newIndustries.push(s);
          });
        } else {
          newIndustries = newIndustries.filter(
            (name) => name !== mainLabel && !subLabels.includes(name)
          );
        }
      } else {
        const subLabel = typeof item === 'string' ? item : `${item.value} (Sub)`;
        const isCurrentlyChecked = newIndustries.includes(subLabel);
        const targetChecked =
          isCheckedOverride !== undefined ? isCheckedOverride : !isCurrentlyChecked;

        if (targetChecked) {
          if (!newIndustries.includes(subLabel)) newIndustries.push(subLabel);
          const subValue = typeof item === 'string' ? item.replace(' (Sub)', '') : item.value;
          const parent = industryData.find((i) =>
            i.sub_industries?.some((s) => s.value === subValue)
          );
          if (parent) {
            const allSubsSelected = parent.sub_industries.every(
              (s) => s.value === subValue || newIndustries.includes(`${s.value} (Sub)`)
            );
            if (allSubsSelected) {
              const mainLabel = `${parent.main_industry} (Main)`;
              if (!newIndustries.includes(mainLabel)) newIndustries.push(mainLabel);
            }
          }
        } else {
          newIndustries = newIndustries.filter((name) => name !== subLabel);
          const subValue = typeof item === 'string' ? item.replace(' (Sub)', '') : item.value;
          const parent = industryData.find((i) =>
            i.sub_industries?.some((s) => s.value === subValue)
          );
          if (parent) {
            const mainLabel = `${parent.main_industry} (Main)`;
            newIndustries = newIndustries.filter((name) => name !== mainLabel);
          }
        }
      }
      return { ...prev, industries: newIndustries };
    });
  };

  // Fetch active analyses on mount and periodically
  const fetchActiveAnalyses = async () => {
    try {
      const response = await axios.get('/market-analysis');
      if (response.data.success) {
        const processing = response.data.data.filter((a) => a.status === 'processing');
        setActiveAnalyses(processing);
        return processing.length > 0;
      }
    } catch (error) {
      console.error('Error fetching active analyses:', error);
    }
    return false;
  };

  useEffect(() => {
    // Check for active analyses whenever room changes
    fetchActiveAnalyses();
  }, [activeChatRoomId]);

  // [CLEANUP] Redundant B2C and Market Analysis Polling useEffects removed.
  // Source of truth is now the orchestrated polling effect at line 1255.

  useEffect(() => {
    let interval;
    // Keep active analyses state fresh
    interval = setInterval(async () => {
      await fetchActiveAnalyses();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!activeChatRoomId || isPollingInChat) return;

    // 1. Recover Market Analysis from global active status
    const roomTask = activeAnalyses.find(
      (a) => String(a.chat_room_id) === String(activeChatRoomId)
    );
    if (roomTask && roomTask.status === 'processing') {
      setMarketAnalysisId(roomTask.id);
      setIsPollingInChat(true);
      setProgressSteps([
        { name: 'Analysis your query...', icon: 'fa-search', status: 'done' },
        { name: 'Searching from web...', icon: 'fa-globe', status: 'active' },
        { name: 'Generating pdf...', icon: 'fa-file-pdf', status: 'pending' },
        { name: 'Exporting pdf...', icon: 'fa-file-export', status: 'pending' },
      ]);
      setActiveProgressStep(1);
      return;
    }

    // 2. Recover B2C Task from last message if actually processing (fallback for B2C which isn't in market_analysis table)
    const lastMsg = [...chatMessages]
      .reverse()
      .find((m) => m.isAi && m.metadata?.isB2CTask && (m.metadata?.task || m.metadata?.data));
    if (lastMsg && (lastMsg.metadata.task?.id || lastMsg.metadata.data?.id)) {
      const taskId = lastMsg.metadata.task?.id || lastMsg.metadata.data?.id;
      // Immediate check status
      axios
        .get(`/b2c/recommendations/${taskId}`)
        .then((res) => {
          if (
            res.data.success &&
            (res.data.data.status === 'processing' || res.data.data.status === 'initializing')
          ) {
            setB2cTask(res.data.data);
            setIsPollingInChat(true);
            setProgressSteps([
              { name: 'Analyzing your intent...', icon: 'fa-brain', status: 'done' },
              { name: 'Identifying persona...', icon: 'fa-user-tag', status: 'done' },
              { name: 'Searching from web...', icon: 'fa-globe', status: 'active' },
              { name: 'Generating recommendations...', icon: 'fa-magic', status: 'pending' },
            ]);
            setActiveProgressStep(2);
          }
        })
        .catch(() => {});
    }
  }, [activeAnalyses.length, activeChatRoomId, isPollingInChat, chatMessages.length]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.custom-dropdown') &&
        !event.target.closest('.chip-input-wrapper')
      ) {
        setExpandedFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStepsForIntent = (intent, isAuto = false) => {
    const steps = [];
    if (isAuto) {
      steps.push({ name: 'Analyzing your intent...', icon: 'fa-brain', status: 'done' });
    }

    const normalizedIntent = (intent || '').toLowerCase().trim();

    if (
      ['search_prospect', 'search_company', 'prospects', 'companies', 'b2b'].includes(
        normalizedIntent
      )
    ) {
      steps.push(
        { name: 'Analyzing query...', icon: 'fa-search', status: 'active' },
        { name: 'Extracting filters...', icon: 'fa-filter', status: 'pending' },
        { name: 'Matching results...', icon: 'fa-list-check', status: 'pending' }
      );
    } else if (['search_persona', 'b2c_persona', 'persona', 'b2c'].includes(normalizedIntent)) {
      steps.push(
        { name: 'Identifying persona...', icon: 'fa-user-astronaut', status: 'active' },
        { name: 'Searching from web...', icon: 'fa-globe', status: 'pending' },
        { name: 'Generating recommendations...', icon: 'fa-magic', status: 'pending' }
      );
    } else if (['market_analysis', 'market', 'market_research'].includes(normalizedIntent)) {
      steps.push(
        { name: 'Analyzing query...', icon: 'fa-magnifying-glass-chart', status: 'active' },
        { name: 'Searching web...', icon: 'fa-globe', status: 'pending' },
        { name: 'Generating pdf...', icon: 'fa-file-pdf', status: 'pending' }
      );
    }
    return steps;
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();

    // Count active filters
    const activeFiltersCount = Object.values(filters).reduce(
      (acc, current) => acc + (current?.length || 0),
      0
    );

    if (!searchQuery.trim() && activeFiltersCount === 0) {
      pushAssistantError(
        'Please enter a search query or select at least one filter before performing discovery.'
      );
      return;
    }

    const isAutoMode = activeMode === 'auto';
    isAutoModeRef.current = isAutoMode;
    let initialSteps = [];
    if (isAutoMode) {
      initialSteps = [{ name: 'Analyzing your intent...', icon: 'fa-brain', status: 'active' }];
    } else {
      initialSteps = getStepsForIntent(activeMode, false);
    }

    setLoading(true);
    setLoadingMessage(isAutoMode ? 'Analyzing your intent...' : 'Thinking...');
    setIsPollingInChat(false);
    setProgressSteps(initialSteps);
    setActiveProgressStep(0);
    setSearchResult(null);
    setB2bResults(null);
    setB2cTask(null);
    setMarketAnalysisId(null);

    const assistantMsgId = `${Date.now()}-ai`;
    const initialMetadata = {
      _steps: initialSteps,
      _intent: isAutoMode ? 'auto' : activeMode,
      status: 'processing',
    };

    // 1. Instantly push both messages in correct order
    const userMsgText = searchQuery.trim() || 'Search via Filters';
    setChatMessages((prev) => [
      ...prev,
      { isAi: false, text: userMsgText },
      {
        id: assistantMsgId,
        isAi: true,
        text: '',
        metadata: initialMetadata,
      },
    ]);
    setSearchQuery(''); // Clear input for next query

    let isBgTask = false;
    let currentIntent = activeMode;
    try {
      const summarizedFilters = { ...filters };
      if (filters.companySizes && filters.companySizes.length > 0) {
        summarizedFilters.companySizes = filters.companySizes;
      }
      if (filters.revenueMin || filters.revenueMax) {
        summarizedFilters.revenues = [
          getSummaryRange(filters.revenueMin, filters.revenueMax, 'revenue'),
        ];
      }

      // Create room if we don't have one and we have a valid chat flow
      let roomId = activeChatRoomId;
      if (!roomId) {
        try {
          const roomRes = await axios.post('/chat', {
            name: searchQuery.substring(0, 50) || 'New Discovery',
          });
          if (roomRes.data.success) {
            isInternalReset.current = true;
            roomId = roomRes.data.data.id;
            setActiveChatRoomId(roomId);
            setCurrentRoomName(roomRes.data.data.name);
            // rewrite the url without reload
            bypassOnce();
            window.history.pushState({}, '', `/discover/${roomId}`);
            // Notify sidebar to refresh
            window.dispatchEvent(new CustomEvent('refresh-chat-list'));
          }
        } catch (e) {
          console.error('Failed to create chat room', e);
        }
      }

      // Transform filters for "Search Company" to match requested API structure
      const isB2BMode = ['b2b', 'search_prospect', 'search_company'].includes(activeMode);
      let finalFilters = isB2BMode
        ? summarizedFilters.isPersona
          ? undefined
          : summarizedFilters
        : undefined;
      if (isB2BMode && searchType === 'companies') {
        const names = filters.companyNames || [];
        const locations = filters.companyLocations || [];

        const sizes = (filters.companySizes || [])
          .map((rangeStr) => {
            const parts = rangeStr
              .replace(/,/g, '')
              .split('-')
              .map((p) => p.trim());
            if (parts.length === 2) {
              return { min: parseInt(parts[0]), max: parseInt(parts[1]) };
            } else if (rangeStr.includes('+')) {
              const val = parseInt(rangeStr.replace(/,/g, '').replace('+', '').trim());
              return { min: val, max: 1000001 }; // Assuming 1M+ as upper bound or high dummy
            }
            return null;
          })
          .filter(Boolean);

        const revenues = [];
        if (filters.revenueMin || filters.revenueMax) {
          revenues.push({
            min: filters.revenueMin ? parseInt(filters.revenueMin) : 0,
            max: filters.revenueMax ? parseInt(filters.revenueMax) : 1000000000,
          });
        }

        const mainIndustriesIds = [];
        const subIndustriesIds = [];
        (filters.industries || []).forEach((indLabel) => {
          const isMain = indLabel.endsWith(' (Main)');
          const name = indLabel.replace(' (Main)', '').replace(' (Sub)', '');

          if (isMain) {
            const match = industryData.find((i) => i.main_industry === name);
            if (match) mainIndustriesIds.push(match.main_industry_id);
          } else {
            industryData.forEach((parent) => {
              const sub = (parent.sub_industries || []).find((s) => s.value === name);
              if (sub) subIndustriesIds.push(sub.id);
            });
          }
        });

        finalFilters = {
          companies: {
            include: {
              names,
              sizes,
              revenues,
              mainIndustriesIds,
              subIndustriesIds,
              locations,
            },
          },
        };
      }

      setLoadingMessage('Thinking...');

      // Simulation timer for synchronous (non-polling) modes
      if (
        !isAutoMode &&
        activeMode !== 'market' &&
        activeMode !== 'market_analysis' &&
        !activeMode.includes('persona')
      ) {
        const totalSteps = initialSteps.length;
        let currentIdx = 0;
        progressIntervalRef.current = setInterval(() => {
          if (currentIdx < totalSteps - 1) {
            currentIdx++;
            setActiveProgressStep(currentIdx);
            setProgressSteps((prev) =>
              prev.map((s, i) => ({
                ...s,
                status: i < currentIdx ? 'done' : i === currentIdx ? 'active' : 'pending',
              }))
            );
          } else {
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
          }
        }, 1500);
      }

      const modeToContext = {
        auto: 'auto',
        search_prospect: 'prospects',
        search_company: 'companies',
        search_persona: 'b2c',
        market_analysis: 'market',
      };

      // 1. PHASE 1: Intent Detection (to show roadmap immediately)
      if (isAutoMode) {
        try {
          const detectResp = await axios.post('/search/detect-intent', {
            searchQuery,
            chatRoomId: roomId,
          });
          currentIntent = detectResp.data.extraction?.intent || detectResp.data.intent;

          // Show full roadmap immediately based on detected intent
          const fullSteps = getStepsForIntent(currentIntent, true);
          setProgressSteps(fullSteps);
          setActiveProgressStep(1);

          // Update the thinking message metadata with the new roadmap
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMsgId
                ? {
                    ...msg,
                    metadata: { ...msg.metadata, _intent: currentIntent, _steps: fullSteps },
                  }
                : msg
            )
          );

          // Sync roadmap timer for B2B sync execution inside Auto mode
          if (currentIntent === 'search_prospect' || currentIntent === 'search_company') {
            let b2bIdx = 1;
            const b2bInt = setInterval(() => {
              if (b2bIdx < 3) {
                b2bIdx++;
                setActiveProgressStep(b2bIdx);
                setProgressSteps((prev) =>
                  prev.map((s, i) => ({
                    ...s,
                    status: i < b2bIdx ? 'done' : i === b2bIdx ? 'active' : 'pending',
                  }))
                );
              } else {
                clearInterval(b2bInt);
              }
            }, 2000);
            progressIntervalRef.current = b2bInt;
          }
        } catch (detectErr) {
          const status = detectErr.response?.status;
          const code = detectErr.response?.data?.code;
          const errMsg = detectErr.response?.data?.error;
          if (
            status === 403 ||
            code === 'POLICY_VIOLATION_BLOCKED' ||
            (errMsg && errMsg.includes('Access blocked by platform security policy'))
          ) {
            // Security policy violation: Abort immediately, do not execute Phase 2
            throw detectErr;
          }
          console.error('Intent detection failed, falling back:', detectErr);
          currentIntent = null;
        }
      }

      // 2. PHASE 2: Check for B2B Search Brief flow
      const isMarketOrB2C =
        activeMode === 'market_analysis' ||
        activeMode === 'search_persona' ||
        currentIntent === 'market_analysis' ||
        currentIntent === 'search_persona' ||
        currentIntent === 'b2c_persona';

      if (!isMarketOrB2C) {
        // --- M01: BRIEFING BEFORE CALLING LUSHA ---
        // Search Brief Builder, Taxonomy/Synonym Expansion & Pre-Flight Check
        const lastBriefMsg = [...chatMessages].reverse().find((m) => m.metadata?.searchBrief);
        const previousBrief = activeBriefRef.current || lastBriefMsg?.metadata?.searchBrief || null;

        const briefResp = await axios.post('/search/brief/generate', {
          searchQuery,
          context: modeToContext[activeMode] || 'prospects',
          chatRoomId: roomId,
          filters: finalFilters,
          previousBrief,
        });

        if (briefResp.data.success && briefResp.data.brief) {
          const brief = briefResp.data.brief;
          activeBriefRef.current = brief;
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMsgId
                ? {
                    ...msg,
                    text: '',
                    metadata: {
                      searchBrief: brief,
                      _intent: currentIntent || activeMode,
                      status: 'brief_ready',
                    },
                  }
                : msg
            )
          );
          setLoading(false);
          setProgressSteps([]);
          setProcessing(false);
          return;
        }
      }

      // Fallback or non-B2B execution (Market Analysis / B2C)
      const response = await axios.post('/search/unified', {
        searchQuery,
        context: modeToContext[activeMode] || 'auto',
        chatRoomId: roomId,
        filters: finalFilters,
        ...(currentIntent ? { intent: currentIntent } : {}),
      });

      const data = response.data;
      setSearchResult(data);
      let aiResponseText = '';
      const intent = data._aiMetadata?.intent || data._intent || currentIntent;

      if (intent === 'market_analysis') {
        if (data.requestId || data.success || data.data?.id) {
          const rid = data.data?.id || data.requestId || data.id;
          setMarketAnalysisId(rid);
          data.requestId = rid;
          data.isMarketAnalysis = true;
          // Provide a clearer thinking message
          aiResponseText = '';

          // Only poll if it's processing
          if (
            data.data?.status === 'processing' ||
            data.data?.status === 'initializing' ||
            data.status === 'processing'
          ) {
            setIsPollingInChat(true);
          }

          const steps = getStepsForIntent(intent, isAutoMode);
          setProgressSteps(steps);
          setActiveProgressStep(isAutoMode ? 1 : 0);
          isBgTask = true;
        } else {
          aiResponseText = data.error || 'Feature not enabled for your account.';
        }
      } else if (intent === 'search_persona' || intent === 'b2c_persona') {
        if (data.success) {
          const task = data.task || data.data;
          setB2cTask(task);
          data.isB2CTask = true;
          aiResponseText = '';

          if (task?.status === 'processing' || task?.status === 'initializing') {
            setIsPollingInChat(true);
          }

          isBgTask = true;
          // Do not call setProgressSteps again if we already have the correct list from intent detection
        } else {
          aiResponseText = data.message || 'Error occurred while creating B2C target.';
        }
      } else if (
        ['search_prospect', 'search_company', 'prospects', 'companies'].includes(intent || '')
      ) {
        // --- B2B RESULTS HANDLER ---
        setB2bResults(data);
        const doneSteps = getStepsForIntent(intent, isAutoMode).map((s) => ({
          ...s,
          status: 'done',
        }));
        setProgressSteps(doneSteps);
      }

      // --- UNIVERSAL MESSAGE RENDERER ---
      // Ensure every search result (B2B, B2C, Market) is added to the chat messages state immediately
      const roadmapSteps = getStepsForIntent(intent, isAutoMode);
      // If it's B2B, ensure the roadmap shown in the bubble is also marked as 'done'
      const finalSteps =
        intent && ['search_prospect', 'search_company', 'prospects', 'companies'].includes(intent)
          ? roadmapSteps.map((s) => ({ ...s, status: 'done' }))
          : roadmapSteps;

      const finalMetadata = {
        ...data,
        _intent: intent,
        _steps: isBgTask || finalSteps.length > 0 ? finalSteps : null,
        isB2CTask: intent === 'search_persona' || intent === 'b2c_persona' || data.isB2CTask,
        isMarketAnalysis: intent === 'market_analysis' || data.isMarketAnalysis,
      };

      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMsgId
            ? {
                ...msg,
                text: aiResponseText,
                metadata: finalMetadata,
              }
            : msg
        )
      );
    } catch (error) {
      const errorData = error.response?.data;
      setChatMessages((prev) => prev.filter((msg) => msg.id !== assistantMsgId));
      if (errorData?.retryAfter) {
        pushAssistantError(`${errorData.message} (retry in ${errorData.retryAfter}s)`);
      } else {
        pushAssistantError(errorData?.error || error.message || 'Search failed. Please try again.');
      }
    } finally {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      // Only stop loading if we're NOT continuing to poll in-chat
      const isBackgroundTaskState =
        ['market_analysis', 'search_persona', 'b2c_persona'].includes(currentIntent) ||
        ['market_analysis', 'search_persona', 'b2c_persona'].includes(activeMode) ||
        isPollingInChat ||
        isBgTask;

      if (!isBackgroundTaskState) {
        setLoading(false);
        setProgressSteps([]);
        setProcessing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Clear previous results and input when mode changes manually
    // Skip if this change was triggered by search auto-detection
    if (ignoreModeReset.current) {
      ignoreModeReset.current = false;
      return;
    }

    setSearchResult(null);
    setB2bResults(null);
    setB2cTask(null);
    setMarketAnalysisId(null);
    setSearchQuery('');
  }, [activeMode]);

  const hasResults = b2bResults || b2cTask || marketAnalysisId;
  const hasResultsOrLoading = hasResults || loading;

  // A line is defined as text followed by a newline, or the current content split by newlines.
  // We treat 25 literal lines (24 newlines) OR ~2500 characters as the limit to handle visual wrapping.
  const isTooLong = (searchQuery.match(/\n/g) || []).length >= 25 || searchQuery.length >= 3000;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close filter dropdowns if click is outside custom-dropdown or filter-group
      if (expandedFilter && !event.target.closest('.custom-dropdown')) {
        setExpandedFilter(null);
      }
      // Close mode dropdown if click is outside mode-plus-container
      if (showModeDropdown && !event.target.closest('.mode-plus-container')) {
        setShowModeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedFilter, showModeDropdown]);

  const handleSimulateBrief = async (msgId, brief) => {
    if (!brief || simulatingBriefId) return;
    setSimulatingBriefId(brief.briefId);
    try {
      const res = await axios.post('/search/brief/simulate', {
        briefId: brief.briefId,
        filters: brief.activeFilters || brief.filters || {},
        searchType: brief.searchType || 'prospects',
      });
      if (res.data.success) {
        setChatMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  metadata: {
                    ...m.metadata,
                    searchBrief: {
                      ...m.metadata.searchBrief,
                      simulation: res.data.simulation,
                      status: 'simulated',
                    },
                  },
                }
              : m
          )
        );
      }
    } catch (err) {
      console.error('Error simulating brief:', err);
      pushAssistantError(
        'Failed to simulate lead count: ' + (err.response?.data?.error || err.message)
      );
    } finally {
      setSimulatingBriefId(null);
    }
  };

  const handleExecuteBrief = async (msgId, brief, count) => {
    if (!brief || executingBriefId) return;
    setExecutingBriefId(brief.briefId);
    try {
      const res = await axios.post('/search/brief/execute', {
        briefId: brief.briefId,
        sampleCount: count,
        filters: brief.activeFilters || brief.filters || {},
        searchType: brief.searchType || 'prospects',
        chatRoomId: activeChatRoomId,
      });

      if (res.data.success) {
        const leadCount =
          res.data.retrievedCount || (res.data.contacts || res.data.companies || []).length;
        setB2bResults(res.data);
        setChatMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  text:
                    'Discovery complete. Retrieved ' +
                    leadCount +
                    ' leads matching your Search Brief.',
                  metadata: {
                    ...m.metadata,
                    ...res.data,
                    searchBrief: {
                      ...m.metadata.searchBrief,
                      status: 'completed',
                      executedCount: count,
                    },
                    status: 'completed',
                    contacts: res.data.contacts,
                    companies: res.data.companies,
                    totalResults: res.data.totalResults,
                    search_brief_id: brief.briefId,
                    briefVersion: res.data.briefVersion || 1,
                  },
                }
              : m
          )
        );
        setExpandedResultId(msgId);
      }
    } catch (err) {
      console.error('Error executing brief:', err);
      pushAssistantError('Failed to retrieve leads: ' + (err.response?.data?.error || err.message));
    } finally {
      setExecutingBriefId(null);
    }
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      setIsFilterClosing(true);
      setTimeout(() => {
        setShowFilters(false);
        setIsFilterClosing(false);
      }, 300);
    } else {
      // If opening filters while in auto mode, switch to Search Prospect as it's the most common B2B entry point
      if (activeMode === 'auto') {
        setActiveMode('search_prospect');
        setSearchType('prospects');
      }
      setShowFilters(true);
    }
  };

  // --- ORCHESTRATED POLLING FOR BACKGROUND TASKS ---
  useEffect(() => {
    if (!isPollingInChat) return;

    const pollInterval = setInterval(async () => {
      try {
        // Determine which task to poll
        if (b2cTask?.id) {
          const res = await axios.get(`/b2c/recommendations/${b2cTask.id}`);
          if (res.data.success) {
            const updatedTask = res.data.data;
            const status = updatedTask.status;
            const step = updatedTask.current_step;

            // Update Roadmap steps
            setProgressSteps((prev) => {
              let currentSteps = [...prev];
              const isAutoDerived = prev[0]?.name?.toLowerCase().includes('intent');

              // SELF-HEALING: If list is empty or short, rebuild it based on task type
              if (currentSteps.length < 3) {
                currentSteps = getStepsForIntent(
                  b2cTask?.id ? 'search_persona' : 'market_analysis',
                  isAutoDerived || isAutoModeRef.current
                );
              }

              const hasIntentRow = currentSteps[0]?.name?.toLowerCase().includes('intent');
              const offset = hasIntentRow ? 1 : 0;

              let stepIdx = offset;
              if (b2cTask?.id) {
                if (step?.includes('Identify') || step?.includes('Persona')) stepIdx = 0 + offset;
                else if (step?.includes('Searching') || step?.includes('web')) stepIdx = 1 + offset;
                else if (step?.includes('Generating') || step?.includes('recommendations'))
                  stepIdx = 2 + offset;
              } else {
                if (step?.includes('Searching') || step?.includes('Tavily')) stepIdx = 1 + offset;
                else if (step?.includes('pdf') || step?.includes('PDF')) stepIdx = 2 + offset;
                else if (step?.includes('Generating answer') || step?.includes('Exporting'))
                  stepIdx = 3 + offset;
              }

              setActiveProgressStep(stepIdx);
              return currentSteps.map((s, i) => ({
                ...s,
                status: i < stepIdx ? 'done' : i === stepIdx ? 'active' : 'pending',
              }));
            });

            if (status === 'completed' || status === 'failed') {
              clearInterval(pollInterval);
              setIsPollingInChat(false);
              setProcessing(false);
              if (b2cTask?.id) setB2cTask(null);
              else setMarketAnalysisId(null);

              // Update global steps one last time to all done
              if (status === 'completed') {
                setProgressSteps((prev) => prev.map((s) => ({ ...s, status: 'done' })));
                setActiveProgressStep(5);
              }

              // Synchronize the message in chat history
              setChatMessages((prev) =>
                prev.map((msg) => {
                  const currentId = b2cTask?.id || marketAnalysisId;
                  const analysisId =
                    msg.metadata?.requestId ||
                    msg.metadata?.data?.id ||
                    msg.metadata?.id ||
                    msg.metadata?.task?.id;
                  if (String(analysisId) === String(currentId)) {
                    return {
                      ...msg,
                      metadata: {
                        ...msg.metadata,
                        status: status,
                        ...(b2cTask?.id
                          ? {
                              task: updatedTask,
                              ideal_persona: updatedTask.ideal_persona,
                              recommendations: updatedTask.recommendations,
                            }
                          : {
                              analysis_result: updatedTask.analysis_result,
                              pdfs: updatedTask.pdfs,
                            }),
                      },
                    };
                  }
                  return msg;
                })
              );
            }
          }
        } else if (marketAnalysisId) {
          const res = await axios.get(`/market-analysis/${marketAnalysisId}`);
          if (res.data.success) {
            const updatedTask = res.data.data;

            // Update Roadmap steps
            const currentStep =
              updatedTask.status === 'completed' ? 3 : updatedTask.status === 'processing' ? 2 : 1;
            setActiveProgressStep(currentStep);
            setProgressSteps((prev) =>
              prev.map((s, i) => ({
                ...s,
                status: i < currentStep ? 'done' : i === currentStep ? 'active' : 'pending',
              }))
            );

            if (updatedTask.status === 'completed' || updatedTask.status === 'failed') {
              clearInterval(pollInterval);
              setIsPollingInChat(false);
              setProcessing(false);
              setMarketAnalysisId(null);

              // Mark all steps as done for completed tasks
              if (updatedTask.status === 'completed') {
                setProgressSteps((prev) => prev.map((s) => ({ ...s, status: 'done' })));
                // Clear the progress indicator after a short delay so user can see completion
                setTimeout(() => {
                  setProgressSteps([]);
                }, 800);
              } else {
                // For failed tasks, clear immediately
                setProgressSteps([]);
              }

              setChatMessages((prev) =>
                prev.map((msg) => {
                  const analysisId =
                    msg.metadata?.requestId || msg.metadata?.data?.id || msg.metadata?.id;
                  if (String(analysisId) === String(marketAnalysisId)) {
                    return {
                      ...msg,
                      metadata: {
                        ...msg.metadata,
                        status: updatedTask.status,
                        analysis_result: updatedTask.analysis_result,
                        pdfs: updatedTask.pdfs,
                      },
                    };
                  }
                  return msg;
                })
              );
            }
          }
        } else {
          // Safety: nothing to poll
          clearInterval(pollInterval);
          setIsPollingInChat(false);
          setProcessing(false);
        }
      } catch (err) {
        console.error('[UnifiedSearch] Polling error:', err);
        // If 404, stop polling
        if (err.response?.status === 404) {
          clearInterval(pollInterval);
          setIsPollingInChat(false);
          setProcessing(false);
        }
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isPollingInChat, b2cTask, marketAnalysisId, setProcessing]);

  const chatEndRef = useRef(null);
  const [loadingMoreId, setLoadingMoreId] = useState(null);

  const handleLoadMoreInChat = async (msg, msgIdx) => {
    if (loadingMoreId) return;

    const metadata = msg.metadata;
    const currentCount = (metadata.contacts || metadata.companies || []).length;
    const totalResults = metadata.totalResults || currentCount;

    if (metadata.hasMore === false && totalResults > 0 && currentCount >= totalResults) return;

    const nextPage =
      metadata.lushaRequestPayload?.page !== undefined
        ? metadata.lushaRequestPayload.page + 1
        : Math.floor(currentCount / 25);

    if (nextPage === (metadata.lushaRequestPayload?.page || 0) && currentCount > 0) {
      // Force increment if logic gets stuck
      // nextPage = (metadata.lushaRequestPayload?.page || 0) + 1;
    }
    setLoadingMoreId(msg.id || msgIdx);

    try {
      const requestParams = {
        searchQuery: metadata._aiMetadata?.searchQuery || searchQuery,
        context: metadata.searchType || 'auto',
        chatRoomId: activeChatRoomId,
        page: nextPage,
        size: 25,
      };

      // If we have the raw lusha payload, use it to skip the AI extraction!
      if (metadata.lushaRequestPayload) {
        requestParams.lushaRequestPayload = {
          ...metadata.lushaRequestPayload,
          page: nextPage,
        };
      } else {
        requestParams.filters = metadata._aiMetadata?.extractedFilters;
      }

      const response = await axios.post('/search/unified', requestParams);

      const newData = response.data;
      let newItems = [];
      if (metadata.searchType === 'companies') {
        newItems =
          newData.companies || (newData.data && newData.data.companies) || newData.data || [];
      } else {
        newItems =
          newData.contacts || (newData.data && newData.data.contacts) || newData.data || [];
      }

      if (newItems && newItems.length > 0) {
        setChatMessages((prev) =>
          prev.map((m, i) => {
            if (i === msgIdx) {
              const updatedMetadata = { ...m.metadata };
              if (metadata.searchType === 'companies') {
                updatedMetadata.companies = [...(metadata.companies || []), ...newItems];
              } else {
                updatedMetadata.contacts = [...(metadata.contacts || []), ...newItems];
              }

              // Sync the payload for next pagination call
              if (newData.lushaRequestPayload) {
                updatedMetadata.lushaRequestPayload = newData.lushaRequestPayload;
              }

              const nextTotalResults = newData.totalResults || totalResults;
              updatedMetadata.totalResults = nextTotalResults;
              const newTotalCount = (updatedMetadata.contacts || updatedMetadata.companies || [])
                .length;
              updatedMetadata.hasMore = newItems.length >= 25 || newTotalCount < nextTotalResults;
              const finalMetadata = updatedMetadata;

              // Persist to database so reload doesn't lose it
              if (m.id) {
                axios
                  .patch(`/chat/messages/${m.id}`, { metadata: finalMetadata })
                  .catch((err) => console.error('Failed to persist paginated results:', err));
              }

              return { ...m, metadata: finalMetadata };
            }
            return m;
          })
        );
      } else {
        setChatMessages((prev) =>
          prev.map((m, i) => {
            if (i === msgIdx) return { ...m, metadata: { ...m.metadata, hasMore: false } };
            return m;
          })
        );
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.retryAfter) {
        pushAssistantError(errData.message || 'Rate limit hit. Please wait.');
      }
      console.error('Error loading more in chat:', err);
    } finally {
      setLoadingMoreId(null);
    }
  };

  const [closingResultId, setClosingResultId] = useState(null);
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isSearchLoading]);

  return (
    <div className={`unified-search-container ${isChatMode ? 'results-view' : ''}`}>
      {/* Processing toast */}
      {showProcessingToast && (
        <div className="processing-toast">
          <div className="processing-toast-content">
            <div className="processing-toast-spinner">
              <i className="fas fa-circle-notch fa-spin"></i>
            </div>
            <span>Please don't leave or refresh the page while AI is processing your request.</span>
          </div>
          <button className="processing-toast-close" onClick={() => setShowProcessingToast(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="history-shortcut-container">
        {activeChatRoomId && currentRoomName && (
          <div className="active-room-display" style={{ zIndex: 50 }}>
            <span className="room-label">Current Chat:</span>
            <span className="room-title">{currentRoomName}</span>
          </div>
        )}
      </div>

      {isChatMode ? (
        <div className="chat-messages-container custom-scrollbar">
          {chatMessages.length === 0 && !isSearchLoading && (
            <div
              className="empty-chat-state"
              style={{ textAlign: 'center', opacity: 0.6, marginTop: '20px' }}
            >
              Start typing a search or instruction above.
            </div>
          )}
          {chatMessages.map((msg, idx) => (
            <div key={idx} className={`chat-message-item ${msg.isAi ? 'assistant' : 'user'}`}>
              <div className="message-identity">
                <div className="author-avatar">
                  {msg.isAi ? (
                    <i className="fa-solid fa-robot"></i>
                  ) : (
                    <i className="fa-solid fa-user"></i>
                  )}
                </div>
                <span className="author-name">{msg.isAi ? 'AI' : 'YOU'}</span>
              </div>
              <div className="message-content-wrapper">
                {(() => {
                  const analysisId =
                    msg.metadata?.requestId || msg.metadata?.data?.id || msg.metadata?.id;
                  const isMarketMsg =
                    msg.isAi && analysisId && isLikelyMarketMetadata(msg.metadata, msg.text);
                  const isB2C =
                    isLikelyB2CMetadata(msg.metadata) &&
                    !isLikelyMarketMetadata(msg.metadata, msg.text);

                  // STANDARD COMPLETION CHECK
                  const showResults = !!(
                    msg.metadata?.contacts?.length > 0 ||
                    msg.metadata?.companies?.length > 0 ||
                    msg.metadata?.ideal_persona ||
                    msg.metadata?.task?.ideal_persona ||
                    msg.metadata?.recommendations ||
                    msg.metadata?.task?.recommendations ||
                    msg.metadata?.analysis_result ||
                    msg.metadata?.pdfs ||
                    msg.metadata?.status === 'completed' ||
                    msg.metadata?.task?.status === 'completed' ||
                    msg.metadata?.data?.status === 'completed'
                  );

                  const isTaskFailed = !!(
                    msg.metadata?.status === 'failed' ||
                    msg.metadata?.task?.status === 'failed' ||
                    msg.metadata?.data?.status === 'failed'
                  );

                  return (
                    <div
                      className={`message-bubble ${msg.isError || isTaskFailed ? 'error' : ''}`}
                      style={
                        msg.isError || isTaskFailed
                          ? {
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              color: '#991b1b',
                              boxShadow: 'none',
                            }
                          : undefined
                      }
                    >
                      {(() => {
                        const taskStatus = msg.metadata?.status || msg.metadata?.data?.status;
                        const isMarketMsgDone = isMarketMsg && taskStatus === 'completed';
                        const failureMessage =
                          msg.metadata?.error_message ||
                          msg.metadata?.error ||
                          msg.metadata?.analysis_result?.error ||
                          msg.metadata?.data?.error ||
                          msg.metadata?.task?.error ||
                          msg.metadata?.task?.error_message;

                        // Suppress common filler headers to keep the UI clean
                        const cleanText =
                          msg.text === 'Here are the search results:' ||
                          msg.text === 'Here are your results.' ||
                          msg.text === 'Here are the search results' ||
                          msg.text === 'Here are your results'
                            ? null
                            : msg.text;

                        if (isTaskFailed && failureMessage) {
                          return failureMessage;
                        }

                        return !isMarketMsgDone && cleanText && cleanText !== '' ? cleanText : null;
                      })()}

                      {/* --- SEARCH BRIEF CARD (M01 Market & Target Definition) --- */}
                      {msg.isAi && msg.metadata?.searchBrief && (
                        <div className="search-brief-card">
                          <div className="brief-card-header">
                            <div className="brief-card-title-group">
                              <div className="brief-header-icon-box">
                                <i className="fa-solid fa-sliders" />
                              </div>
                              <span className="brief-header-title">
                                Target Criteria
                                <span className="brief-version-tag">
                                  v{msg.metadata.searchBrief.version || 1}
                                </span>
                              </span>
                              {msg.metadata.searchBrief.version > 1 && (
                                <span
                                  className="brief-refinement-badge"
                                  title={msg.metadata.searchBrief.refinementNotes}
                                >
                                  <i className="fa-solid fa-check" /> Refined
                                </span>
                              )}
                            </div>
                            <div className="brief-header-meta">
                              <span className="brief-type-badge">
                                {msg.metadata.searchBrief.searchType === 'companies'
                                  ? 'Companies'
                                  : 'Prospects'}
                              </span>
                            </div>
                          </div>

                          {msg.metadata.searchBrief.refinementNotes ? (
                            <div className="brief-refinement-banner">
                              <i className="fa-solid fa-circle-check" />
                              <span>
                                <strong>Updated:</strong> {msg.metadata.searchBrief.refinementNotes}
                              </span>
                            </div>
                          ) : (
                            msg.metadata.searchBrief.rawQuery && (
                              <div className="brief-raw-query">
                                <i className="fa-solid fa-quote-left query-quote-icon" />
                                <span>{msg.metadata.searchBrief.rawQuery}</span>
                              </div>
                            )
                          )}

                          <div className="brief-spec-table">
                            {/* 1. Target Roles */}
                            {(msg.metadata.searchBrief.target?.originalRoles?.length > 0 ||
                              msg.metadata.searchBrief.target?.expandedRoles?.length > 0) &&
                              (() => {
                                const allExpanded = (
                                  msg.metadata.searchBrief.target?.expandedRoles || []
                                ).filter(
                                  (r) =>
                                    !(
                                      msg.metadata.searchBrief.target?.originalRoles || []
                                    ).includes(r)
                                );
                                const displayExpanded = allExpanded;
                                const isExpanded = expandedSynonymsId === msg.id + '-roles';
                                return (
                                  <div className="brief-spec-row">
                                    <div className="brief-spec-row-main">
                                      <div className="brief-spec-label">
                                        <i className="fa-solid fa-user-tie" />
                                        <span>Role</span>
                                      </div>
                                      <div className="brief-spec-content">
                                        {(msg.metadata.searchBrief.target?.originalRoles?.length > 0
                                          ? msg.metadata.searchBrief.target.originalRoles
                                          : ['Any Role']
                                        ).map((r, i) => (
                                          <span
                                            key={'orig-r-' + i}
                                            className="brief-tag original role"
                                          >
                                            {r}
                                          </span>
                                        ))}
                                        {displayExpanded.length > 0 && (
                                          <button
                                            type="button"
                                            className="brief-synonym-toggle-btn"
                                            onClick={() =>
                                              setExpandedSynonymsId(
                                                isExpanded ? null : msg.id + '-roles'
                                              )
                                            }
                                          >
                                            <span>
                                              {isExpanded
                                                ? 'less'
                                                : `+${displayExpanded.length} more`}
                                            </span>
                                            <i
                                              className={
                                                'fa-solid fa-chevron-' +
                                                (isExpanded ? 'up' : 'down') +
                                                ' toggle-icon'
                                              }
                                            />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isExpanded && displayExpanded.length > 0 && (
                                      <div className="brief-spec-expanded-row">
                                        {displayExpanded.map((syn, idx) => (
                                          <span key={'exp-r-' + idx} className="brief-tag expanded">
                                            {syn}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                            {/* 2. Industry & Sectors */}
                            {(msg.metadata.searchBrief.target?.originalIndustries?.length > 0 ||
                              msg.metadata.searchBrief.target?.expandedIndustries?.length > 0) &&
                              (() => {
                                const allExpanded = (
                                  msg.metadata.searchBrief.target?.expandedIndustries || []
                                ).filter(
                                  (ind) =>
                                    !(
                                      msg.metadata.searchBrief.target?.originalIndustries || []
                                    ).includes(ind)
                                );
                                const displayExpanded = allExpanded;
                                const isExpanded = expandedSynonymsId === msg.id + '-industry';
                                return (
                                  <div className="brief-spec-row">
                                    <div className="brief-spec-row-main">
                                      <div className="brief-spec-label">
                                        <i className="fa-solid fa-building" />
                                        <span>Industry</span>
                                      </div>
                                      <div className="brief-spec-content">
                                        {(msg.metadata.searchBrief.target?.originalIndustries
                                          ?.length > 0
                                          ? msg.metadata.searchBrief.target.originalIndustries
                                          : ['Any Industry']
                                        ).map((ind, i) => (
                                          <span
                                            key={'orig-ind-' + i}
                                            className="brief-tag original industry"
                                          >
                                            {ind}
                                          </span>
                                        ))}
                                        {displayExpanded.length > 0 && (
                                          <button
                                            type="button"
                                            className="brief-synonym-toggle-btn"
                                            onClick={() =>
                                              setExpandedSynonymsId(
                                                isExpanded ? null : msg.id + '-industry'
                                              )
                                            }
                                          >
                                            <span>
                                              {isExpanded
                                                ? 'less'
                                                : `+${displayExpanded.length} more`}
                                            </span>
                                            <i
                                              className={
                                                'fa-solid fa-chevron-' +
                                                (isExpanded ? 'up' : 'down') +
                                                ' toggle-icon'
                                              }
                                            />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {isExpanded && displayExpanded.length > 0 && (
                                      <div className="brief-spec-expanded-row">
                                        {displayExpanded.map((sec, idx) => (
                                          <span
                                            key={'exp-ind-' + idx}
                                            className="brief-tag expanded"
                                          >
                                            {sec}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                            {/* 3. Location & Company Size */}
                            <div className="brief-spec-row">
                              <div className="brief-spec-row-main">
                                <div className="brief-spec-label">
                                  <i className="fa-solid fa-location-dot" />
                                  <span>Location</span>
                                </div>
                                <div className="brief-spec-content">
                                  <span className="brief-tag original location">
                                    {msg.metadata.searchBrief.target?.locations?.length > 0
                                      ? msg.metadata.searchBrief.target.locations.join(', ')
                                      : 'Global (All locations)'}
                                  </span>
                                  {msg.metadata.searchBrief.target?.companySizes?.length > 0 && (
                                    <span className="brief-spec-size-badge">
                                      <i className="fa-solid fa-users" />{' '}
                                      {msg.metadata.searchBrief.target.companySizes.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {msg.metadata.searchBrief.missingInfo?.length > 0 && (
                            <div className="brief-missing-box">
                              <i className="fa-regular fa-lightbulb brief-missing-icon" />
                              <div className="brief-missing-content">
                                <span className="brief-gap-desc">
                                  {msg.metadata.searchBrief.missingInfo[0].message}
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="brief-simulation-section">
                            {!msg.metadata.searchBrief.simulation ? (
                              <div className="brief-sim-prompt">
                                <button
                                  className="brief-action-btn simulate"
                                  onClick={() =>
                                    handleSimulateBrief(msg.id, msg.metadata.searchBrief)
                                  }
                                  disabled={simulatingBriefId === msg.metadata.searchBrief.briefId}
                                >
                                  {simulatingBriefId === msg.metadata.searchBrief.briefId ? (
                                    <>
                                      <i className="fa-solid fa-spinner fa-spin" /> Checking Lead
                                      Count...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa-solid fa-magnifying-glass" /> Check Matching
                                      Leads
                                    </>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div className="brief-sim-results">
                                <div className="brief-sim-header-row">
                                  <div className="brief-sim-count-badge">
                                    <i className="fa-solid fa-circle-check" />
                                    <span>
                                      Found{' '}
                                      <strong>
                                        {Number(
                                          msg.metadata.searchBrief.simulation.totalResults || 0
                                        ).toLocaleString()}
                                      </strong>{' '}
                                      matching leads
                                    </span>
                                  </div>
                                </div>

                                {msg.metadata.searchBrief.status !== 'completed' && (
                                  <div className="brief-sample-picker">
                                    <span className="brief-picker-label">Retrieve sample:</span>
                                    <div className="brief-sample-buttons">
                                      {(
                                        msg.metadata.searchBrief.simulation.sampleOptions || []
                                      ).map((opt, i) => (
                                        <button
                                          key={i}
                                          className={`brief-sample-option-btn ${i === 0 ? 'primary-sample' : ''}`}
                                          onClick={() =>
                                            handleExecuteBrief(
                                              msg.id,
                                              msg.metadata.searchBrief,
                                              opt.count
                                            )
                                          }
                                          disabled={
                                            executingBriefId === msg.metadata.searchBrief.briefId
                                          }
                                        >
                                          {executingBriefId === msg.metadata.searchBrief.briefId ? (
                                            <i className="fa-solid fa-spinner fa-spin" />
                                          ) : (
                                            <i className="fa-solid fa-arrow-down-to-bracket" />
                                          )}
                                          <span>{opt.label}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {msg.metadata.searchBrief.status === 'completed' && (
                                  <div className="brief-executed-banner">
                                    <i className="fa-solid fa-circle-check" />
                                    <span>
                                      Showing{' '}
                                      {msg.metadata.searchBrief.executedCount ||
                                        (msg.metadata.contacts || msg.metadata.companies || [])
                                          .length}{' '}
                                      leads below
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* --- UNIFIED PROGRESS ROADMAP --- */}
                      {msg.isAi && msg.metadata?._steps && !showResults && (
                        <div
                          className="thinking-bubble-steps inline-thinking"
                          style={{
                            marginTop: '0',
                            padding: '0 0 8px 0',
                            borderBottom: 'none',
                            marginBottom: '0',
                          }}
                        >
                          <div className="thinking-steps-list">
                            {(() => {
                              const taskMetadata = isLikelyB2CMetadata(msg.metadata)
                                ? msg.metadata.task || msg.metadata.data || msg.metadata
                                : msg.metadata;
                              const analysisId =
                                msg.metadata?.requestId ||
                                msg.metadata?.data?.id ||
                                msg.metadata?.id;

                              const isThisActiveTask =
                                isPollingInChat &&
                                ((isLikelyB2CMetadata(msg.metadata) &&
                                  String(taskMetadata?.id) === String(b2cTask?.id)) ||
                                  (isLikelyMarketMetadata(msg.metadata, msg.text) &&
                                    String(analysisId) === String(marketAnalysisId)));

                              const stepsToRender = isThisActiveTask
                                ? progressSteps
                                : msg.metadata._steps || [];

                              return stepsToRender.map((step, i) => (
                                <div
                                  key={i}
                                  className={`thinking-step-row thinking-step-${step.status}`}
                                  style={{ animation: 'stepFadeIn 0.4s ease-out' }}
                                >
                                  <div
                                    className={`thinking-step-icon thinking-step-icon-${step.status}`}
                                  >
                                    <i
                                      className={`fa-solid ${step.status === 'done' ? 'fa-check' : step.status === 'active' ? 'fa-spinner fa-spin' : step.icon}`}
                                    ></i>
                                  </div>
                                  <div className="thinking-step-label">{step.name}</div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}

                      {msg.isAi && msg.metadata && showResults && (
                        <div
                          className="discovery-summary-chat"
                          style={{ marginTop: msg.metadata?._steps ? '20px' : '12px' }}
                        >
                          {msg.metadata.totalResults ? (
                            <p style={{ margin: '0 0 10px 0', fontSize: '15px' }}>
                              I found{' '}
                              <strong>
                                {Number(msg.metadata.totalResults || 0).toLocaleString()}
                              </strong>{' '}
                              potential matches for your request by searching with the following
                              filters:
                            </p>
                          ) : null}
                          <div style={{ paddingLeft: '4px' }}>
                            {(() => {
                              const f =
                                msg.metadata._aiMetadata?.filters ||
                                msg.metadata._aiMetadata?.extractedFilters ||
                                {};
                              const summary = [];

                              // Dedup and normalize keys
                              const seniority = [
                                ...new Set([...(f.seniority || []), ...(f.managementLevels || [])]),
                              ];
                              const contactLocs = [
                                ...new Set([...(f.contactLocations || []), ...(f.locations || [])]),
                              ];

                              if (f.jobTitles?.length > 0)
                                summary.push(`Job titles: ${f.jobTitles.join(', ')}`);
                              if (seniority.length > 0)
                                summary.push(`Seniority: ${seniority.join(', ')}`);
                              if (f.departments?.length > 0)
                                summary.push(`Departments: ${f.departments.join(', ')}`);
                              if (contactLocs.length > 0)
                                summary.push(`Contact location: ${contactLocs.join(', ')}`);
                              if (f.companyNames?.length > 0)
                                summary.push(`Companies: ${f.companyNames.join(', ')}`);
                              if (f.companyLocations?.length > 0)
                                summary.push(`Company location: ${f.companyLocations.join(', ')}`);
                              if (f.industries?.length > 0)
                                summary.push(`Industries: ${f.industries.join(', ')}`);
                              if (f.companySizes?.length > 0)
                                summary.push(`Company size: ${f.companySizes.join(', ')}`);

                              // Market Analysis & B2C specific filters
                              if (f.marketQuery) summary.push(`Market Segment: ${f.marketQuery}`);
                              if (f.locations?.length > 0 && !contactLocs.length)
                                summary.push(`Geographic Focus: ${f.locations.join(', ')}`);
                              if (f.productDescription)
                                summary.push(`Product Focus: ${f.productDescription}`);
                              if (f.targetRegion && !contactLocs.length)
                                summary.push(`Target Region: ${f.targetRegion}`);

                              // B2C Persona specifics (Message form)
                              if (f.interests?.length > 0)
                                summary.push(`Interests: ${f.interests.join(', ')}`);
                              if (f.demographics?.ageRange)
                                summary.push(`Age Range: ${f.demographics.ageRange}`);
                              if (f.demographics?.lifestyle)
                                summary.push(`Lifestyle: ${f.demographics.lifestyle}`);
                              if (f.summary) summary.push(`Summary: ${f.summary}`);

                              return summary.map((line, lidx) => (
                                <div
                                  key={lidx}
                                  style={{
                                    fontSize: '14px',
                                    color: '#475569',
                                    marginBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '8px',
                                  }}
                                >
                                  <div style={{ color: '#2563eb', fontWeight: '800' }}>•</div>
                                  <div>{line}</div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}

                      {msg.isAi &&
                        isLikelyB2CMetadata(msg.metadata) &&
                        !isLikelyMarketMetadata(msg.metadata, msg.text) &&
                        !isTaskFailed && (
                          <B2CRecommendationView
                            userId={userId}
                            refreshProfile={refreshProfile}
                            initialTask={msg.metadata.task || msg.metadata.data || msg.metadata}
                            hideNewAnalysis={true}
                            hideSteps={true}
                          />
                        )}

                      {msg.metadata &&
                        (msg.metadata.contacts || msg.metadata.companies) &&
                        !msg.metadata.isB2CTask && (
                          <div
                            className="discovery-actions-compact"
                            style={{
                              marginTop: '16px',
                              paddingTop: '16px',
                              borderTop: '1px solid #f1f5f9',
                              display: 'flex',
                              gap: '12px',
                            }}
                          >
                            <button
                              className={`view-results-btn-compact ${expandedResultId === (msg.id || idx) ? 'expanded' : ''}`}
                              onClick={() =>
                                setExpandedResultId(
                                  expandedResultId === (msg.id || idx) ? null : msg.id || idx
                                )
                              }
                              style={{
                                background:
                                  expandedResultId === (msg.id || idx) ? '#f1f5f9' : '#3b82f6',
                                color: expandedResultId === (msg.id || idx) ? '#475569' : 'white',
                                border:
                                  expandedResultId === (msg.id || idx)
                                    ? '1px solid #e2e8f0'
                                    : 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                              }}
                            >
                              <i
                                className={`fa-solid ${expandedResultId === (msg.id || idx) ? 'fa-eye-slash' : 'fa-list-ul'}`}
                              ></i>
                              {expandedResultId === (msg.id || idx) ? 'Hide List' : 'View List'}
                            </button>
                          </div>
                        )}

                      {msg.isAi &&
                        msg.metadata &&
                        (msg.metadata.contacts || msg.metadata.companies) &&
                        (expandedResultId === (msg.id || idx) ||
                          closingResultId === (msg.id || idx)) && (
                          <div
                            className={`discovery-results-wrapper historical-results ${msg.isAi ? 'is-ai-message' : 'is-user-message'} ${closingResultId === (msg.id || idx) ? 'is-closing' : ''}`}
                          >
                            <ProspectFinderResults
                              briefId={
                                msg.metadata.search_brief_id || msg.metadata.searchBrief?.briefId
                              }
                              prospects={msg.metadata.contacts || msg.metadata.companies || []}
                              searchType={
                                msg.metadata.searchType ||
                                (msg.metadata._intent === 'search_company'
                                  ? 'companies'
                                  : msg.metadata.companies && !msg.metadata.contacts
                                    ? 'companies'
                                    : 'prospects')
                              }
                              setSearchType={() => {}}
                              loading={loadingMoreId === (msg.id || idx)}
                              requestId={msg.metadata.requestId}
                              chatMessageId={msg.id || msg.metadata?.chat_message_id}
                              userId={userId}
                              onSearch={handleSearch}
                              onRefresh={() => {}}
                              onBack={() => setExpandedResultId(null)}
                              searchQuery={''}
                              setSearchQuery={() => {}}
                              refreshProfile={refreshProfile}
                              searchProvider="lusha"
                              setSearchProvider={() => {}}
                              hasMore={
                                msg.metadata.hasMore !== undefined
                                  ? msg.metadata.hasMore
                                  : msg.metadata.totalResults >
                                    (msg.metadata.contacts || msg.metadata.companies || []).length
                              }
                              totalResults={msg.metadata.totalResults}
                              lushaRequestPayload={msg.metadata.lushaRequestPayload}
                              onLoadMore={() => handleLoadMoreInChat(msg, idx)}
                            />
                          </div>
                        )}

                      {msg.isAi &&
                        isLikelyMarketMetadata(msg.metadata, msg.text) &&
                        !isLikelyB2CMetadata(msg.metadata) &&
                        !isTaskFailed && (
                          <div
                            className="discovery-results-wrapper chat-bubble-results historical-results is-ai-message"
                            style={{
                              marginTop: '0',
                              border: 'none',
                              background: 'transparent',
                              boxShadow: 'none',
                            }}
                          >
                            <div style={{ width: '100%', maxWidth: '100%' }}>
                              <MarketAnalysisChatView
                                analysisId={
                                  msg.metadata?.requestId ||
                                  msg.metadata?.data?.id ||
                                  msg.metadata?.id
                                }
                                hideSteps={true}
                                onComplete={(data) => {
                                  if (data.status === 'completed') {
                                    setCompletedAnalysisIds(
                                      (prev) => new Set([...prev, String(data.id)])
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
          {/* Animated thinking / progress indicator */}
          {/* Animated thinking for INITIAL search (B2B / B2C start) */}
          {(() => {
            const isAnyInlineProcessing = chatMessages.some((m) => {
              const task = m.metadata?.task || m.metadata?.data || m.metadata;
              const isTaskProcessing =
                task?.status === 'processing' || task?.status === 'initializing';
              return m.isAi && isTaskProcessing;
            });

            // ONLY show the global bubble if we are loading AND no inline bubble has taken over yet
            if (isSearchLoading && !isAnyInlineProcessing) {
              return (
                <div className="chat-message-item assistant fadeIn animated">
                  <div className="message-identity">
                    <div className="author-avatar">
                      <i className="fa-solid fa-robot"></i>
                    </div>
                    <span className="author-name">AI</span>
                  </div>
                  <div className="message-content-wrapper">
                    <div
                      className="thinking-bubble thinking-bubble-steps"
                      style={{
                        padding: '16px 20px 16px 14px',
                        border: '1px solid #e2e8f0',
                        background: 'white',
                        borderRadius: '20px',
                        borderTopLeftRadius: '4px',
                      }}
                    >
                      {progressSteps.length > 0 ? (
                        <div className="thinking-steps-list">
                          {progressSteps.map((step, i) => (
                            <div
                              key={i}
                              className={`thinking-step-row thinking-step-${step.status}`}
                              style={{
                                animation: 'stepFadeIn 0.4s ease-out',
                                opacity: step.status === 'pending' ? 0.6 : 1,
                              }}
                            >
                              <div
                                className={`thinking-step-icon thinking-step-icon-${step.status}`}
                              >
                                <i
                                  className={`fa-solid ${step.status === 'done' ? 'fa-check' : step.status === 'active' ? 'fa-spinner fa-spin' : step.icon}`}
                                ></i>
                              </div>
                              <div className="thinking-step-label">{step.name}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="thinking-progress-message">
                          {loadingMessage}
                          <div
                            className="thinking-dots-container"
                            style={{ display: 'inline-flex', marginLeft: '6px' }}
                          >
                            <div className="thinking-dot"></div>
                            <div className="thinking-dot"></div>
                            <div className="thinking-dot"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
          <div ref={chatEndRef} />
        </div>
      ) : (
        <div className={`search-header ${showFilters ? 'filters-active' : ''}`}>
          <h1>What would you like to discover today?</h1>
          <p>Search for prospects, companies, or market trends with the power of AI.</p>
        </div>
      )}

      <form
        onSubmit={handleSearch}
        className={`unified-search-bar-container ${isChatMode ? 'is-chat-mode' : ''} ${showFilters || isFilterClosing ? 'split-layout' : ''} ${isFilterClosing ? 'closing' : ''}`}
      >
        <div className="search-bar-panel">
          <div className={`search-input-wrapper ${isMultiline ? 'is-multiline' : ''}`}>
            <div className="left-tools">
              <div className="mode-plus-container">
                <button
                  type="button"
                  className={`mode-plus-btn ${activeMode !== 'auto' ? 'active' : ''}`}
                  onClick={() => setShowModeDropdown(!showModeDropdown)}
                >
                  <i className={`fa-solid fa-${showModeDropdown ? 'minus' : 'plus'}`}></i>
                </button>
                {showModeDropdown && (
                  <div className="mode-dropdown-menu fadeIn animated">
                    {/* 1. Search Prospect */}
                    <div
                      className={`mode-dropdown-item ${activeMode === 'search_prospect' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveMode('search_prospect');
                        setSearchType('prospects');
                        resetFilters();
                        setShowModeDropdown(false);
                      }}
                    >
                      <i className="fa-solid fa-users"></i> Search Prospect
                      <div className="custom-instant-tooltip">
                        Find B2B decision makers & professionals
                      </div>
                    </div>

                    {/* 3. Search Persona */}
                    <div
                      className={`mode-dropdown-item ${activeMode === 'search_persona' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveMode('search_persona');
                        resetFilters();
                        setShowModeDropdown(false);
                      }}
                    >
                      <i className="fa-solid fa-globe"></i> Search Persona{' '}
                      <div className="custom-instant-tooltip">Identify consumer persona</div>
                    </div>

                    {/* 4. Market Analysis */}
                    <div
                      className={`mode-dropdown-item ${activeMode === 'market_analysis' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveMode('market_analysis');
                        resetFilters();
                        setShowModeDropdown(false);
                      }}
                    >
                      <i className="fa-solid fa-chart-line"></i> Market Analysis{' '}
                      <div className="custom-instant-tooltip">
                        Deep-dive market analysis & industry reports
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {activeMode !== 'auto' && (
                <div
                  className={`active-mode-indicator ${!searchQuery.trim() ? 'no-input' : ''}`}
                  onClick={() => {
                    setActiveMode('auto');
                    setShowFilters(false);
                    setSearchType('prospects');
                  }}
                >
                  {activeMode === 'search_prospect'
                    ? 'PROSPECT'
                    : activeMode === 'search_company'
                      ? 'COMPANY'
                      : activeMode === 'search_persona'
                        ? 'PERSONA'
                        : 'MARKET'}
                  <i
                    className="fa-solid fa-times"
                    style={{ marginLeft: '6px', fontSize: '0.8em', opacity: 0.7 }}
                  ></i>
                </div>
              )}
            </div>

            <textarea
              ref={textareaRef}
              className="search-textarea"
              placeholder={processingActive ? '' : placeholder}
              value={processingActive ? '' : searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => {
                setExpandedFilter(null);
                setShowModeDropdown(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch(e);
                }
              }}
              disabled={processingActive}
              rows={1}
            />
            {processingActive && (
              <div className="processing-input-overlay">
                <i className="fa-solid fa-circle-notch fa-spin"></i>
                <span>
                  The AI is thinking and processing, please wait. It will take a while
                  {'.'.repeat(processingDotCount)}
                </span>
              </div>
            )}

            <div className="right-tools">
              {['search_prospect', 'search_company', 'b2b'].includes(activeMode) && (
                <button
                  type="button"
                  className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
                  onClick={handleToggleFilters}
                >
                  <i className="fa-solid fa-filter"></i>
                  <span>Filter</span>
                </button>
              )}

              <div
                className="search-btn-wrapper"
                style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <button
                  type="submit"
                  className="search-btn"
                  disabled={
                    processingActive ||
                    isTooLong ||
                    (!searchQuery.trim() && !Object.values(filters).some((arr) => arr?.length > 0))
                  }
                  style={{
                    cursor: processingActive || isTooLong ? 'not-allowed' : 'pointer',
                    backgroundColor: processingActive || isTooLong ? '#cbd5e1' : '#3b82f6',
                    color: processingActive || isTooLong ? '#94a3b8' : 'white',
                  }}
                >
                  {processingActive ? (
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                  ) : (
                    <i className="fa-solid fa-arrow-up" aria-hidden="true"></i>
                  )}
                </button>

                {isTooLong && showTooltip && (
                  <div className="custom-instant-tooltip">Message too long</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="search-modes-wrapper" style={{ display: 'none' }}>
          {/* Old modes hidden, replacement UI is inside search-input-wrapper */}
        </div>

        {['search_prospect', 'search_company', 'b2b'].includes(activeMode) &&
          (showFilters || isFilterClosing) && (
            <div
              className={`discovery-filters-container ${isFilterClosing ? 'closing' : 'opening'}`}
            >
              <div className="filters-header">
                <div className="filters-title">Filter</div>
                <button type="button" className="clear-filters" onClick={resetFilters}>
                  <i className="fa-solid fa-rotate-left"></i> Reset
                </button>
              </div>

              <div className="filters-content">
                {searchType !== 'companies' && (
                  <>
                    <div className="filters-section contact-section">
                      <h3 className="section-label">Contact</h3>
                      <div className="filters-grid">
                        {/* Top Row: Contact Name */}
                        <div className="filter-group">
                          <label>Contact Name</label>
                          <div className="chip-input-wrapper">
                            <div className="chip-container">
                              {(filters.contactName || []).map((name) => (
                                <span key={name} className="filter-chip">
                                  {name}{' '}
                                  <i
                                    className="fa-solid fa-xmark"
                                    onClick={() => removeFilter('contactName', name)}
                                  ></i>
                                </span>
                              ))}
                            </div>
                            <input
                              type="text"
                              placeholder="Type then enter..."
                              style={{
                                flex: 1,
                                minWidth: '150px',
                                outline: 'none',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '0.85rem',
                              }}
                              onFocus={() => {
                                setExpandedFilter(null);
                                setShowModeDropdown(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = e.target.value.trim();
                                  if (val) {
                                    addFilter('contactName', val);
                                    e.target.value = '';
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>

                        {/* Second Row: Dept & Seniority */}
                        <div
                          className={`filter-row ${['contact_department', 'seniority'].includes(expandedFilter) ? 'active-row' : ''}`}
                          style={{
                            zIndex: ['contact_department', 'seniority'].includes(expandedFilter)
                              ? 100
                              : 1,
                          }}
                        >
                          <div
                            className={`filter-group ${expandedFilter === 'contact_department' ? 'active-group' : ''}`}
                          >
                            <label>Department</label>
                            <div className="custom-dropdown">
                              <div className="chip-container">
                                {(filters.departments || []).map((dept) => (
                                  <span key={dept} className="filter-chip">
                                    {dept}{' '}
                                    <i
                                      className="fa-solid fa-xmark"
                                      onClick={() => removeFilter('departments', dept)}
                                    ></i>
                                  </span>
                                ))}
                              </div>
                              <input
                                type="text"
                                placeholder="Search departments..."
                                value={departmentSearch || ''}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  flex: 1,
                                  minWidth: '150px',
                                  outline: 'none',
                                  fontSize: '0.85rem',
                                }}
                                onChange={(e) => {
                                  setDepartmentSearch(e.target.value);
                                  setExpandedFilter('contact_department');
                                }}
                                onFocus={() => {
                                  setExpandedFilter('contact_department');
                                  setShowModeDropdown(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const opt = [
                                      'Business Development',
                                      'Consulting',
                                      'Customer Service',
                                      'Engineering & Technical',
                                      'Finance',
                                      'General Management',
                                      'Health Care & Medical',
                                      'Human Resources',
                                      'Information Technology',
                                      'Legal',
                                      'Marketing',
                                      'Operations',
                                      'Other',
                                      'Product',
                                      'Research & Analytics',
                                      'Sales',
                                    ];
                                    const match =
                                      opt.find(
                                        (o) => o.toLowerCase() === departmentSearch.toLowerCase()
                                      ) ||
                                      opt.find((o) =>
                                        o.toLowerCase().includes(departmentSearch.toLowerCase())
                                      );
                                    if (match) addFilter('departments', match);
                                    setDepartmentSearch('');
                                  }
                                }}
                              />
                              <i
                                className={`fa-solid fa-chevron-${expandedFilter === 'contact_department' ? 'up' : 'down'}`}
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#94a3b8',
                                  marginLeft: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  setExpandedFilter(
                                    expandedFilter === 'contact_department'
                                      ? null
                                      : 'contact_department'
                                  )
                                }
                              ></i>
                              {expandedFilter === 'contact_department' && (
                                <div
                                  className="leadgen-custom-menu"
                                  style={{
                                    display: 'block',
                                    padding: '0',
                                    width: 'max-content',
                                    minWidth: '100%',
                                    left: 0,
                                    margin: 0,
                                    top: '100%',
                                    maxHeight: '160px',
                                    minHeight: 'auto',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0 0 12px 12px',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                    zIndex: 2000,
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  {(() => {
                                    const options = [
                                      'Business Development',
                                      'Consulting',
                                      'Customer Service',
                                      'Engineering & Technical',
                                      'Finance',
                                      'General Management',
                                      'Health Care & Medical',
                                      'Human Resources',
                                      'Information Technology',
                                      'Legal',
                                      'Marketing',
                                      'Operations',
                                      'Other',
                                      'Product',
                                      'Research & Analytics',
                                      'Sales',
                                    ];
                                    const filtered = options.filter((d) =>
                                      d.toLowerCase().includes(departmentSearch.toLowerCase())
                                    );
                                    if (filtered.length > 0) {
                                      return filtered.map((d) => {
                                        const isChecked = filters.departments.includes(d);
                                        return (
                                          <div
                                            key={d}
                                            style={{
                                              display: 'grid',
                                              gridTemplateColumns: '22px 1fr',
                                              alignItems: 'center',
                                              width: '100%',
                                              padding: '4px 8px',
                                              gap: '2px',
                                              borderLeft: isChecked
                                                ? '4px solid #2563eb'
                                                : '4px solid transparent',
                                              minHeight: '32px',
                                              cursor: 'pointer',
                                              background: isChecked ? '#eff6ff' : 'white',
                                              boxSizing: 'border-box',
                                            }}
                                            onMouseDown={(e) => {
                                              e.preventDefault(); // Prevent focus loss
                                              if (isChecked) removeFilter('departments', d);
                                              else addFilter('departments', d);
                                            }}
                                            className="leadgen-grid-row"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              readOnly
                                              style={{
                                                margin: 0,
                                                justifySelf: 'center',
                                                width: '13px',
                                                height: '13px',
                                                cursor: 'pointer',
                                              }}
                                            />
                                            <span
                                              style={{
                                                fontSize: '0.85rem',
                                                fontWeight: isChecked ? '600' : '400',
                                                color: isChecked ? '#2563eb' : '#334155',
                                                whiteSpace: 'nowrap',
                                                opacity: 1,
                                                visibility: 'visible',
                                                display: 'block',
                                                paddingRight: '12px',
                                              }}
                                            >
                                              {d}
                                            </span>
                                          </div>
                                        );
                                      });
                                    } else {
                                      return (
                                        <div
                                          style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            color: '#64748b',
                                            fontSize: '0.85rem',
                                          }}
                                        >
                                          No selection found
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className={`filter-group ${expandedFilter === 'seniority' ? 'active-group' : ''}`}
                          >
                            <label>Seniority</label>
                            <div className="custom-dropdown">
                              <div className="chip-container">
                                {(filters.managementLevels || []).map((level) => (
                                  <span key={level} className="filter-chip">
                                    {level}{' '}
                                    <i
                                      className="fa-solid fa-xmark"
                                      onClick={() => removeFilter('managementLevels', level)}
                                    ></i>
                                  </span>
                                ))}
                              </div>
                              <input
                                type="text"
                                placeholder="Search seniority..."
                                value={senioritySearch || ''}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  flex: 1,
                                  minWidth: '150px',
                                  outline: 'none',
                                  fontSize: '0.85rem',
                                }}
                                onChange={(e) => {
                                  setSenioritySearch(e.target.value);
                                  setExpandedFilter('seniority');
                                }}
                                onFocus={() => {
                                  setExpandedFilter('seniority');
                                  setShowModeDropdown(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const opt = [
                                      'Founder',
                                      'CEO',
                                      'C-suite',
                                      'VP',
                                      'Vice President',
                                      'Partner',
                                      'Director',
                                      'Manager',
                                      'Senior',
                                      'Junior',
                                      'Entry',
                                      'Intern',
                                      'Other',
                                    ];
                                    const match =
                                      opt.find(
                                        (o) => o.toLowerCase() === senioritySearch.toLowerCase()
                                      ) ||
                                      opt.find((o) =>
                                        o.toLowerCase().includes(senioritySearch.toLowerCase())
                                      );
                                    if (match) addFilter('managementLevels', match);
                                    setSenioritySearch('');
                                  }
                                }}
                              />
                              <i
                                className={`fa-solid fa-chevron-${expandedFilter === 'seniority' ? 'up' : 'down'}`}
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#94a3b8',
                                  marginLeft: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  setExpandedFilter(
                                    expandedFilter === 'seniority' ? null : 'seniority'
                                  )
                                }
                              ></i>
                              {expandedFilter === 'seniority' && (
                                <div
                                  className="leadgen-custom-menu"
                                  style={{
                                    display: 'block',
                                    padding: '0',
                                    width: 'max-content',
                                    minWidth: '100%',
                                    left: 0,
                                    margin: 0,
                                    top: '100%',
                                    maxHeight: '160px',
                                    minHeight: 'auto',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0 0 12px 12px',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                    zIndex: 2000,
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  {(() => {
                                    const options = [
                                      'Founder',
                                      'CEO',
                                      'C-suite',
                                      'VP',
                                      'Vice President',
                                      'Partner',
                                      'Director',
                                      'Manager',
                                      'Senior',
                                      'Junior',
                                      'Entry',
                                      'Intern',
                                      'Other',
                                    ];
                                    const filtered = options.filter((level) =>
                                      level.toLowerCase().includes(senioritySearch.toLowerCase())
                                    );
                                    if (filtered.length > 0) {
                                      return filtered.map((level) => {
                                        const isChecked = filters.managementLevels.includes(level);
                                        return (
                                          <div
                                            key={level}
                                            style={{
                                              display: 'grid',
                                              gridTemplateColumns: '22px 1fr',
                                              alignItems: 'center',
                                              width: '100%',
                                              padding: '4px 8px',
                                              gap: '2px',
                                              borderLeft: isChecked
                                                ? '4px solid #2563eb'
                                                : '4px solid transparent',
                                              minHeight: '32px',
                                              cursor: 'pointer',
                                              background: isChecked ? '#eff6ff' : 'white',
                                              boxSizing: 'border-box',
                                            }}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              if (isChecked)
                                                removeFilter('managementLevels', level);
                                              else addFilter('managementLevels', level);
                                            }}
                                            className="leadgen-grid-row"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              readOnly
                                              style={{
                                                margin: 0,
                                                justifySelf: 'center',
                                                width: '13px',
                                                height: '13px',
                                                cursor: 'pointer',
                                              }}
                                            />
                                            <span
                                              style={{
                                                fontSize: '0.85rem',
                                                fontWeight: isChecked ? '600' : '400',
                                                color: isChecked ? '#2563eb' : '#334155',
                                                whiteSpace: 'nowrap',
                                                opacity: 1,
                                                visibility: 'visible',
                                                display: 'block',
                                                paddingRight: '12px',
                                              }}
                                            >
                                              {level}
                                            </span>
                                          </div>
                                        );
                                      });
                                    } else {
                                      return (
                                        <div
                                          style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            color: '#64748b',
                                            fontSize: '0.85rem',
                                          }}
                                        >
                                          No selection found
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Third Row: Location & Job Title */}
                        <div
                          className={`filter-row ${['contact_location'].includes(expandedFilter) ? 'active-row' : ''}`}
                          style={{
                            zIndex: ['contact_location'].includes(expandedFilter) ? 100 : 1,
                          }}
                        >
                          <div
                            className={`filter-group ${expandedFilter === 'contact_location' ? 'active-group' : ''}`}
                          >
                            <label>Location</label>
                            <div className="custom-dropdown">
                              <div className="chip-container">
                                {filters.contactLocations.map((loc) => (
                                  <span key={loc} className="filter-chip">
                                    {loc}{' '}
                                    <i
                                      className="fa-solid fa-xmark"
                                      onClick={() => removeFilter('contactLocations', loc)}
                                    ></i>
                                  </span>
                                ))}
                              </div>
                              <input
                                type="text"
                                placeholder="Search location..."
                                value={contactLocationSearch}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  flex: 1,
                                  minWidth: '150px',
                                  outline: 'none',
                                  fontSize: '0.85rem',
                                }}
                                onChange={(e) => {
                                  setContactLocationSearch(e.target.value);
                                  setExpandedFilter('contact_location');
                                }}
                                onFocus={() => {
                                  setExpandedFilter('contact_location');
                                  setShowModeDropdown(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const filtered = countriesData.filter((c) =>
                                      c.name
                                        .toLowerCase()
                                        .includes(contactLocationSearch.toLowerCase())
                                    );
                                    if (filtered.length > 0) {
                                      addFilter('contactLocations', filtered[0].name);
                                    }
                                    setContactLocationSearch('');
                                  }
                                }}
                              />
                              <i
                                className={`fa-solid fa-chevron-${expandedFilter === 'contact_location' ? 'up' : 'down'}`}
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#94a3b8',
                                  marginLeft: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  setExpandedFilter(
                                    expandedFilter === 'contact_location'
                                      ? null
                                      : 'contact_location'
                                  )
                                }
                              ></i>
                              {expandedFilter === 'contact_location' && (
                                <div
                                  className="leadgen-custom-menu"
                                  style={{
                                    display: 'block',
                                    padding: '0',
                                    width: 'max-content',
                                    minWidth: '100%',
                                    left: 0,
                                    margin: 0,
                                    top: '100%',
                                    maxHeight: '160px',
                                    minHeight: 'auto',
                                    overflowY: 'auto',
                                    position: 'absolute',
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0 0 12px 12px',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                    zIndex: 2000,
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  {(() => {
                                    const filtered = countriesData.filter((c) =>
                                      c.name
                                        .toLowerCase()
                                        .includes(contactLocationSearch.toLowerCase())
                                    );
                                    if (filtered.length > 0) {
                                      return filtered.map((c) => {
                                        const isChecked = filters.contactLocations.includes(c.name);
                                        return (
                                          <div
                                            key={c.code}
                                            style={{
                                              display: 'grid',
                                              gridTemplateColumns: '22px 1fr',
                                              alignItems: 'center',
                                              width: '100%',
                                              padding: '4px 8px',
                                              gap: '2px',
                                              borderLeft: isChecked
                                                ? '4px solid #2563eb'
                                                : '4px solid transparent',
                                              minHeight: '32px',
                                              cursor: 'pointer',
                                              background: isChecked ? '#eff6ff' : 'white',
                                              boxSizing: 'border-box',
                                            }}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              if (isChecked)
                                                removeFilter('contactLocations', c.name);
                                              else addFilter('contactLocations', c.name);
                                            }}
                                            className="leadgen-grid-row"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              readOnly
                                              style={{
                                                margin: 0,
                                                justifySelf: 'center',
                                                width: '13px',
                                                height: '13px',
                                                cursor: 'pointer',
                                              }}
                                            />
                                            <span
                                              style={{
                                                fontSize: '0.85rem',
                                                fontWeight: isChecked ? '600' : '400',
                                                color: isChecked ? '#2563eb' : '#334155',
                                                whiteSpace: 'nowrap',
                                                opacity: 1,
                                                visibility: 'visible',
                                                display: 'block',
                                                paddingRight: '12px',
                                              }}
                                            >
                                              {c.name}{' '}
                                              <span style={{ color: '#999', fontSize: '0.8em' }}>
                                                ({c.code})
                                              </span>
                                            </span>
                                          </div>
                                        );
                                      });
                                    } else {
                                      return (
                                        <div
                                          style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            color: '#64748b',
                                            fontSize: '0.85rem',
                                          }}
                                        >
                                          No selection found
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="filter-group">
                            <label>Job Title</label>
                            <div className="chip-input-wrapper">
                              <div className="chip-container">
                                {filters.jobTitles.map((job) => (
                                  <span key={job} className="filter-chip">
                                    {job}{' '}
                                    <i
                                      className="fa-solid fa-xmark"
                                      onClick={() => removeFilter('jobTitles', job)}
                                    ></i>
                                  </span>
                                ))}
                              </div>
                              <input
                                type="text"
                                placeholder="Type then enter..."
                                style={{
                                  flex: 1,
                                  minWidth: '150px',
                                  outline: 'none',
                                  border: 'none',
                                  background: 'transparent',
                                  fontSize: '0.85rem',
                                }}
                                onFocus={() => {
                                  setExpandedFilter(null);
                                  setShowModeDropdown(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = e.target.value.trim();
                                    if (val) {
                                      addFilter('jobTitles', val);
                                      e.target.value = '';
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="filters-divider"></div>
                  </>
                )}

                <div className="filters-section company-section">
                  <h3 className="section-label">Company</h3>
                  <div className="filters-grid">
                    {/* Top Row: Company Name */}
                    <div className="filter-group">
                      <label>Company Name</label>
                      <div className="chip-input-wrapper">
                        <div className="chip-container">
                          {(filters.companyNames || []).map((comp) => (
                            <span key={comp} className="filter-chip">
                              {comp}{' '}
                              <i
                                className="fa-solid fa-xmark"
                                onClick={() => removeFilter('companyNames', comp)}
                              ></i>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder="Type then enter..."
                          style={{
                            flex: 1,
                            minWidth: '150px',
                            outline: 'none',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '0.85rem',
                          }}
                          onFocus={() => {
                            setExpandedFilter(null);
                            setShowModeDropdown(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = e.target.value.trim();
                              if (val) {
                                addFilter('companyNames', val);
                                e.target.value = '';
                              }
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Second Row: Industry & Size */}
                    <div
                      className={`filter-row ${['industry', 'comp_size'].includes(expandedFilter) ? 'active-row' : ''}`}
                      style={{
                        zIndex: ['industry', 'comp_size'].includes(expandedFilter) ? 100 : 1,
                      }}
                    >
                      <div
                        className={`filter-group ${expandedFilter === 'industry' ? 'active-group' : ''}`}
                      >
                        <label>Industry</label>
                        <div className="custom-dropdown industry-tree-dropdown">
                          <div className="chip-container">
                            {(() => {
                              const display = [];
                              industryData.forEach((parent) => {
                                const isMainSelected = filters.industries.includes(
                                  `${parent.main_industry} (Main)`
                                );
                                const allSubsSelected = parent.sub_industries?.every((s) =>
                                  filters.industries.includes(`${s.value} (Sub)`)
                                );
                                if (isMainSelected || allSubsSelected) {
                                  display.push({
                                    label: parent.main_industry,
                                    type: 'Main',
                                    parent,
                                  });
                                } else {
                                  parent.sub_industries?.forEach((s) => {
                                    if (filters.industries.includes(`${s.value} (Sub)`)) {
                                      display.push({ label: s.value, type: 'Sub', item: s });
                                    }
                                  });
                                }
                              });
                              return display.map(({ label, type, parent, item }) => (
                                <span key={`${label}-${type}`} className="filter-chip">
                                  {label}{' '}
                                  <i
                                    className="fa-solid fa-xmark"
                                    onClick={() => {
                                      if (type === 'Main')
                                        handleIndustryToggle(parent, true, false);
                                      else handleIndustryToggle(item, false, false);
                                    }}
                                  ></i>
                                </span>
                              ));
                            })()}
                          </div>
                          <input
                            type="text"
                            placeholder="Search industries..."
                            value={industrySearch || ''}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              flex: 1,
                              minWidth: '150px',
                              outline: 'none',
                              fontSize: '0.85rem',
                            }}
                            onChange={(e) => {
                              setIndustrySearch(e.target.value);
                              setExpandedFilter('industry');
                            }}
                            onFocus={() => {
                              setExpandedFilter('industry');
                              setShowModeDropdown(false);
                            }}
                          />
                          <i
                            className={`fa-solid fa-chevron-${expandedFilter === 'industry' ? 'up' : 'down'}`}
                            style={{
                              fontSize: '0.7rem',
                              color: '#94a3b8',
                              marginLeft: '8px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              setExpandedFilter(expandedFilter === 'industry' ? null : 'industry')
                            }
                          ></i>
                          {expandedFilter === 'industry' && (
                            <div
                              className="leadgen-custom-menu"
                              style={{
                                display: 'block',
                                padding: '0',
                                width: 'max-content',
                                minWidth: '100%',
                                left: 0,
                                margin: 0,
                                top: '100%',
                                maxHeight: '160px',
                                minHeight: 'auto',
                                overflowY: 'auto',
                                position: 'absolute',
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0 0 12px 12px',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                zIndex: 2000,
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {(() => {
                                const filteredItems = industryData
                                  .map((item) => {
                                    if (!industrySearch.trim()) return item;
                                    const searchLower = industrySearch.toLowerCase();
                                    const mainMatch = item.main_industry
                                      .toLowerCase()
                                      .includes(searchLower);
                                    const matchingSubs = (item.sub_industries || []).filter((sub) =>
                                      sub.value.toLowerCase().includes(searchLower)
                                    );
                                    if (mainMatch) return item;
                                    if (matchingSubs.length > 0)
                                      return {
                                        ...item,
                                        sub_industries: matchingSubs,
                                        forceExpand: true,
                                      };
                                    return null;
                                  })
                                  .filter(Boolean);

                                if (filteredItems.length > 0) {
                                  return filteredItems.map((item, idx) => {
                                    const isExpanded =
                                      item.forceExpand || expandedIndustries[item.main_industry];
                                    const isMainChecked = filters.industries.includes(
                                      `${item.main_industry} (Main)`
                                    );

                                    return (
                                      <div
                                        key={`${item.main_industry}-${idx}`}
                                        style={{ display: 'block', width: '100%' }}
                                      >
                                        <div
                                          style={{
                                            display: 'grid',
                                            gridTemplateColumns: '32px 32px 1fr',
                                            alignItems: 'center',
                                            padding: '2px 8px',
                                            minHeight: '30px',
                                            cursor: 'pointer',
                                            background: isMainChecked ? '#eff6ff' : 'transparent',
                                            borderLeft: isMainChecked
                                              ? '4px solid #3b82f6'
                                              : '4px solid transparent',
                                            boxSizing: 'border-box',
                                            gap: '0',
                                          }}
                                          className="leadgen-grid-row"
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleIndustryToggle(item, true);
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              width: '32px',
                                              height: '30px',
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: '26px',
                                                height: '26px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                background: isExpanded ? '#eff6ff' : 'transparent',
                                                color: isExpanded ? '#2563eb' : '#94a3b8',
                                              }}
                                              className="expander-caret-btn"
                                              onMouseDown={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                const mainName = item.main_industry;
                                                setExpandedIndustries((prev) => ({
                                                  ...prev,
                                                  [mainName]: !prev[mainName],
                                                }));
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                              }}
                                            >
                                              <i
                                                className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'}`}
                                                style={{ fontSize: '0.85rem' }}
                                              ></i>
                                            </div>
                                          </div>
                                          <input
                                            type="checkbox"
                                            checked={isMainChecked}
                                            style={{
                                              width: '16px',
                                              height: '16px',
                                              margin: 0,
                                              justifySelf: 'center',
                                              cursor: 'pointer',
                                            }}
                                            readOnly
                                          />
                                          <span
                                            style={{
                                              flex: 1,
                                              fontSize: '0.85rem',
                                              color: isMainChecked ? '#2563eb' : '#334155',
                                              fontWeight: isMainChecked ? '600' : '400',
                                              whiteSpace: 'nowrap',
                                              display: 'block',
                                              opacity: 1,
                                              paddingRight: '12px',
                                              paddingLeft: '8px',
                                            }}
                                          >
                                            {item.main_industry}
                                          </span>
                                        </div>
                                        {isExpanded && item.sub_industries && (
                                          <div style={{ display: 'block' }}>
                                            {item.sub_industries.map((sub, sidx) => {
                                              const isSubChecked = filters.industries.includes(
                                                `${sub.value} (Sub)`
                                              );
                                              return (
                                                <div
                                                  key={sidx}
                                                  style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '50px 32px 1fr',
                                                    alignItems: 'center',
                                                    padding: '2px 8px',
                                                    minHeight: '28px',
                                                    borderLeft: isSubChecked
                                                      ? '4px solid #3b82f6'
                                                      : '4px solid transparent',
                                                    cursor: 'pointer',
                                                    background: isSubChecked
                                                      ? '#f8fafc'
                                                      : 'transparent',
                                                    boxSizing: 'border-box',
                                                    gap: '0',
                                                  }}
                                                  className="leadgen-grid-row"
                                                  onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleIndustryToggle(sub, false);
                                                  }}
                                                >
                                                  <div></div>
                                                  <input
                                                    type="checkbox"
                                                    checked={isSubChecked}
                                                    style={{
                                                      width: '13px',
                                                      height: '13px',
                                                      margin: 0,
                                                      justifySelf: 'center',
                                                      cursor: 'pointer',
                                                    }}
                                                    readOnly
                                                  />
                                                  <span
                                                    style={{
                                                      fontSize: '0.85rem',
                                                      color: isSubChecked ? '#3b82f6' : '#64748b',
                                                      fontWeight: isSubChecked ? '600' : '400',
                                                      whiteSpace: 'nowrap',
                                                      display: 'block',
                                                      opacity: 1,
                                                      paddingLeft: '8px',
                                                    }}
                                                  >
                                                    {sub.value}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                } else {
                                  return (
                                    <div
                                      style={{
                                        padding: '12px',
                                        textAlign: 'center',
                                        color: '#64748b',
                                        fontSize: '0.85rem',
                                      }}
                                    >
                                      No selection found
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          )}
                        </div>
                      </div>

                      <div
                        className={`filter-group ${expandedFilter === 'comp_size' ? 'active-group' : ''}`}
                      >
                        <label>Size</label>
                        <div className="custom-dropdown">
                          <input
                            type="text"
                            placeholder="Select sizes..."
                            value={filters.companySizes.join(', ')}
                            readOnly
                            style={{
                              flex: 1,
                              minWidth: '150px',
                              outline: 'none',
                              border: 'none',
                              background: 'transparent',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              setExpandedFilter(expandedFilter === 'comp_size' ? null : 'comp_size')
                            }
                            onFocus={() => {
                              setExpandedFilter('comp_size');
                              setShowModeDropdown(false);
                            }}
                          />
                          <i
                            className={`fa-solid fa-chevron-${expandedFilter === 'comp_size' ? 'up' : 'down'}`}
                            style={{
                              fontSize: '0.7rem',
                              color: '#94a3b8',
                              marginLeft: '8px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              setExpandedFilter(expandedFilter === 'comp_size' ? null : 'comp_size')
                            }
                          ></i>
                          {expandedFilter === 'comp_size' && (
                            <div
                              className="leadgen-custom-menu"
                              style={{
                                display: 'block',
                                padding: '8px',
                                width: '220px',
                                left: 0,
                                top: '100%',
                                maxHeight: '160px',
                                overflowY: 'auto',
                                position: 'absolute',
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                zIndex: 2000,
                              }}
                            >
                              {[
                                '1 - 10',
                                '11 - 50',
                                '51 - 200',
                                '201 - 500',
                                '501 - 1,000',
                                '1,001 - 5,000',
                                '5,001 - 10,000',
                                '10,001 - 100,000',
                                '100,001+',
                              ].map((size) => {
                                const isChecked = filters.companySizes.includes(size);
                                return (
                                  <div
                                    key={size}
                                    className="checkbox-item"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '6px 8px',
                                      cursor: 'pointer',
                                      borderRadius: '6px',
                                      background: isChecked ? '#eff6ff' : 'transparent',
                                      transition: 'all 0.2s',
                                    }}
                                    onClick={() => {
                                      if (isChecked)
                                        setFilters((prev) => ({
                                          ...prev,
                                          companySizes: prev.companySizes.filter((s) => s !== size),
                                        }));
                                      else
                                        setFilters((prev) => ({
                                          ...prev,
                                          companySizes: [...prev.companySizes, size],
                                        }));
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '18px',
                                        height: '18px',
                                        border: isChecked
                                          ? '2px solid #2563eb'
                                          : '2px solid #cbd5e1',
                                        borderRadius: '4px',
                                        marginRight: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: isChecked ? '#2563eb' : 'white',
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      {isChecked && (
                                        <i
                                          className="fa-solid fa-check"
                                          style={{ color: 'white', fontSize: '10px' }}
                                        ></i>
                                      )}
                                    </div>
                                    <span
                                      style={{
                                        fontSize: '0.85rem',
                                        color: isChecked ? '#2563eb' : '#334155',
                                      }}
                                    >
                                      {size}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Third Row: Location & Revenue */}
                    <div
                      className={`filter-row ${['company_location', 'rev_range'].includes(expandedFilter) ? 'active-row' : ''}`}
                      style={{
                        zIndex: ['company_location', 'rev_range'].includes(expandedFilter)
                          ? 100
                          : 1,
                      }}
                    >
                      <div
                        className={`filter-group ${expandedFilter === 'company_location' ? 'active-group' : ''}`}
                      >
                        <label>Location</label>
                        <div className="custom-dropdown">
                          <div className="chip-container">
                            {(filters.companyLocations || []).map((loc) => (
                              <span key={loc} className="filter-chip">
                                {loc}{' '}
                                <i
                                  className="fa-solid fa-xmark"
                                  onClick={() => removeFilter('companyLocations', loc)}
                                ></i>
                              </span>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="Search location..."
                            value={companyLocationSearch || ''}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              flex: 1,
                              minWidth: '150px',
                              outline: 'none',
                              fontSize: '0.85rem',
                            }}
                            onChange={(e) => {
                              setCompanyLocationSearch(e.target.value);
                              setExpandedFilter('company_location');
                            }}
                            onFocus={() => {
                              setExpandedFilter('company_location');
                              setShowModeDropdown(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const filtered = countriesData.filter((c) =>
                                  c.name.toLowerCase().includes(companyLocationSearch.toLowerCase())
                                );
                                if (filtered.length > 0)
                                  addFilter('companyLocations', filtered[0].name);
                                setCompanyLocationSearch('');
                              }
                            }}
                          />
                          <i
                            className={`fa-solid fa-chevron-${expandedFilter === 'company_location' ? 'up' : 'down'}`}
                            style={{
                              fontSize: '0.7rem',
                              color: '#94a3b8',
                              marginLeft: '8px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              setExpandedFilter(
                                expandedFilter === 'company_location' ? null : 'company_location'
                              )
                            }
                          ></i>
                          {expandedFilter === 'company_location' && (
                            <div
                              className="leadgen-custom-menu"
                              style={{
                                display: 'block',
                                padding: '0',
                                width: 'max-content',
                                minWidth: '100%',
                                left: 0,
                                margin: 0,
                                top: '100%',
                                maxHeight: '160px',
                                minHeight: 'auto',
                                overflowY: 'auto',
                                position: 'absolute',
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0 0 12px 12px',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                                zIndex: 2000,
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {countriesData.filter((c) =>
                                c.name.toLowerCase().includes(companyLocationSearch.toLowerCase())
                              ).length > 0 ? (
                                countriesData
                                  .filter((c) =>
                                    c.name
                                      .toLowerCase()
                                      .includes(companyLocationSearch.toLowerCase())
                                  )
                                  .map((c) => {
                                    const isChecked = filters.companyLocations.includes(c.name);
                                    return (
                                      <div
                                        key={c.code}
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: '22px 1fr',
                                          alignItems: 'center',
                                          width: '100%',
                                          padding: '4px 8px',
                                          gap: '2px',
                                          borderLeft: isChecked
                                            ? '4px solid #2563eb'
                                            : '4px solid transparent',
                                          minHeight: '32px',
                                          cursor: 'pointer',
                                          background: isChecked ? '#eff6ff' : 'white',
                                          boxSizing: 'border-box',
                                        }}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          if (isChecked) removeFilter('companyLocations', c.name);
                                          else addFilter('companyLocations', c.name);
                                        }}
                                        className="leadgen-grid-row"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          readOnly
                                          style={{
                                            margin: 0,
                                            justifySelf: 'center',
                                            width: '13px',
                                            height: '13px',
                                            cursor: 'pointer',
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontSize: '0.85rem',
                                            fontWeight: isChecked ? '600' : '400',
                                            color: isChecked ? '#2563eb' : '#334155',
                                            whiteSpace: 'nowrap',
                                            opacity: 1,
                                            visibility: 'visible',
                                            display: 'block',
                                            paddingRight: '12px',
                                          }}
                                        >
                                          {c.name}{' '}
                                          <span style={{ color: '#999', fontSize: '0.8em' }}>
                                            ({c.code})
                                          </span>
                                        </span>
                                      </div>
                                    );
                                  })
                              ) : (
                                <div
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    color: '#64748b',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  No selection found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div
                        className={`filter-group ${expandedFilter === 'rev_range' ? 'active-group' : ''}`}
                      >
                        <label>Revenue</label>
                        <div
                          className="min-max-filter-container"
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            height: '44px',
                          }}
                        >
                          <select
                            value={filters.revenueMin}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, revenueMin: e.target.value }))
                            }
                            style={{
                              flex: 1,
                              height: '100%',
                              padding: '0 10px',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              fontSize: '0.85rem',
                              outline: 'none',
                              background: 'white',
                              color: filters.revenueMin ? '#334155' : '#94a3b8',
                              fontFamily: 'inherit',
                            }}
                            onFocus={() => setExpandedFilter('rev_range')}
                          >
                            <option value="">Min</option>
                            {[
                              '$1M',
                              '$10M',
                              '$50M',
                              '$100M',
                              '$250M',
                              '$500M',
                              '$1B',
                              '$10B',
                              '$100B',
                            ].map((val) => (
                              <option key={val} value={val}>
                                {val}
                              </option>
                            ))}
                          </select>
                          <span style={{ color: '#94a3b8' }}>-</span>
                          <select
                            value={filters.revenueMax}
                            onChange={(e) =>
                              setFilters((prev) => ({ ...prev, revenueMax: e.target.value }))
                            }
                            style={{
                              flex: 1,
                              height: '100%',
                              padding: '0 10px',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              fontSize: '0.85rem',
                              outline: 'none',
                              background: 'white',
                              color: filters.revenueMax ? '#334155' : '#94a3b8',
                              fontFamily: 'inherit',
                            }}
                            onFocus={() => setExpandedFilter('rev_range')}
                          >
                            <option value="">Max</option>
                            {[
                              '$1M',
                              '$10M',
                              '$50M',
                              '$100M',
                              '$250M',
                              '$500M',
                              '$1B',
                              '$10B',
                              '$100B',
                            ].map((val) => (
                              <option key={val} value={val}>
                                {val}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
      </form>

      {!isChatMode && !showFilters && <div style={{ marginBottom: '60px' }}></div>}

      {/* Market analysis overlay removed — processing and results are now shown inline in the chatroom */}
    </div>
  );
};

export default UnifiedLeadGenView;
